import { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import apiService from '../services/api'
import { useStage } from '../contexts/StageContext'
import IdeaProgress from './IdeaProgress'
import IdeaWhiteboard from './IdeaWhiteboard'
import TaskManager from './TaskManager'
import PageHeader from './PageHeader'
import RefineIdeaModal from './RefineIdeaModal'
import AuriHelpModal from './AuriHelpModal'
import AuriFeedbackModal from './AuriFeedbackModal'
import Footer from './Footer'
import { StageNavigationV6 } from './StageNavigationVariations'
import { formatAssessmentReason } from '../utils/textFormatting'
import { STAGE_ASSISTANT_MAP, ASSISTANTS, getOrinPoseForScore, getOrinScoreMessage, getOrinSizeClass, getOrinTopPosition } from '../data/assistants'
import NextStepsPanel from './NextStepsPanel'
import AskOrinModal from './AskOrinModal'
import { parseAssessmentSections, parseUSPSections, parseMarketDemandSections, generateSwotWhiteboard } from '../utils/orinInsights'
import { createTLSchema } from '@tldraw/tldraw'

// Helper functions for score-based styling
const getScoreBackgroundClass = (score) => {
  if (score > 6) return 'bg-green-500/20 border-green-500/30'
  if (score > 4) return 'bg-yellow-500/20 border-yellow-500/30'
  return 'bg-red-500/20 border-red-500/30'
}

const getScoreGradientClass = (score) => {
  if (score > 6) return 'bg-gradient-to-br from-green-500 to-green-600'
  if (score > 4) return 'bg-gradient-to-br from-yellow-500 to-yellow-600'
  return 'bg-gradient-to-br from-red-500 to-red-600'
}

const getScoreTextClass = (score) => {
  if (score > 6) return 'text-green-400'
  if (score > 4) return 'text-yellow-400'
  return 'text-red-400'
}

const getScoreBorderClass = (score) => {
  if (score >= 7) return '!border-green-500'
  if (score >= 5) return '!border-yellow-500'
  return '!border-red-500'
}

const getSaturationBorderClass = (score) => {
  if (score > 6) return '!border-red-500'
  if (score > 5) return '!border-yellow-500'
  return '!border-green-500'
}

const getSaturationTextClass = (score) => {
  if (score < 5) return 'text-green-400'
  if (score < 7) return 'text-yellow-400'
  return 'text-red-400'
}

const getScoreLabel = (score) => {
  if (score >= 10) return 'Exceptional'
  if (score >= 7) return 'Strong'
  if (score >= 5) return 'Moderate'
  return 'Weak'
}

const getStatusColor = (status) => {
  if (status === 'completed') return 'text-green-600'
  if (status === 'triage' || status === 'processing') return 'text-blue-600'
  if (status === 'idea-refinement' || status === 'needs-refinement') return 'text-orange-600'
  return 'text-gray-600'
}

const getStatusBgColor = (status) => {
  if (status === 'completed') return 'bg-green-100'
  if (status === 'triage' || status === 'processing') return 'bg-blue-100'
  if (status === 'idea-refinement' || status === 'needs-refinement') return 'bg-orange-100'
  return 'bg-gray-100'
}

// Helper functions for stage progress (used in team cards)
const isStageComplete = (stageId, currentIdea) => {
  if (stageId === 1) {
    return currentIdea?.opportunity_assessment?.score !== undefined && currentIdea?.opportunity_assessment?.score !== null
  }
  return false
}

const getStageStatus = (stageId, currentStage, currentIdea) => {
  if (isStageComplete(stageId, currentIdea)) return 'completed'
  // Stage 1 (Orin) is "current" (working) when idea is in any of these in-progress statuses
  const inProgressStatuses = [
    'triage',
    'opportunity-assessment',
    'search-terms-generated',
    'web-searches-completed',
    'content-extraction-completed',
    'market-demand-evaluated',
    'market-saturation-evaluated',
    'usp-evaluated'
  ]
  // For stage 1 (Orin), show as "current" if:
  // - status is explicitly in the in-progress list, OR
  // - status is undefined/null (initial load before data refresh) AND score not yet set
  if (stageId === 1 && !isStageComplete(stageId, currentIdea)) {
    if (inProgressStatuses.includes(currentIdea?.status) || !currentIdea?.status) {
      return 'current'
    }
  }
  return 'locked'
}

const getStageProgress = (stageId, currentIdea) => {
  if (stageId === 1) {
    let completed = 0
    if (currentIdea?.triage_assessment || 
        currentIdea?.status === 'opportunity-assessment' ||
        currentIdea?.status === 'search-terms-generated' ||
        currentIdea?.status === 'market-demand-evaluated' ||
        currentIdea?.status === 'market-saturation-evaluated' ||
        currentIdea?.status === 'usp-evaluated' ||
        currentIdea?.status === 'opportunity-assessed') completed++
    if (currentIdea?.market_demand_evaluation) completed++
    if (currentIdea?.market_saturation_evaluation) completed++
    if (currentIdea?.usp_evaluation) completed++
    if (currentIdea?.opportunity_assessment) completed++
    return (completed / 5) * 100
  }
  return 0
}

const getProgressBarColor = (stageId, currentStage, currentIdea) => {
  const status = getStageStatus(stageId, currentStage, currentIdea)
  const progress = getStageProgress(stageId, currentIdea)
  if (status === 'completed') return 'bg-green-500'
  if (status === 'current' || progress > 0) return 'bg-gradient-to-r from-blue-500 to-blue-600'
  return 'bg-gray-700'
}

function IdeaDetail({ idea, onBack, onCreateIdea, onCreateIdeaChat, onLogout }) {
  const { currentStage, goToStage, resetProgress } = useStage()
  const [currentIdea, setCurrentIdea] = useState(idea)
  // Debug: expose currentIdea to window for console access (remove after debugging)
  if (typeof window !== 'undefined') window.__debugIdea = currentIdea
  const [history, setHistory] = useState([])
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    what: idea.what || '',
    who: idea.who || '',
    why: idea.why || '',
    what_expected: idea.what_expected || '',
    make_money: idea.make_money || false,
    positive_examples: idea.positive_examples || '',
    negative_examples: idea.negative_examples || ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [isAssessmentExpanded, setIsAssessmentExpanded] = useState(false)
  const [expandedResearchSections, setExpandedResearchSections] = useState({
    marketTerms: false,
    webSearch: false,
    contentExtraction: false
  })
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(idea.openTasksTab ? 'tasks' : 'details')
  const [expandedHistorySections, setExpandedHistorySections] = useState({
    thisWeek: false,
    thisMonth: false,
    older: false
  })
  const [reports, setReports] = useState([])
  const [loadingReports, setLoadingReports] = useState(false)
  const [whiteboardData, setWhiteboardData] = useState(null)
  const [whiteboardLoadedForIdeaId, setWhiteboardLoadedForIdeaId] = useState(null) // Track which idea whiteboard was loaded for
  const [whiteboardVersion, setWhiteboardVersion] = useState(0) // Incremented to force tldraw remount
  // Derive loading state - immediately false when idea.id changes, no effect needed
  const isWhiteboardLoaded = whiteboardLoadedForIdeaId === idea.id
  const [isPinned, setIsPinned] = useState(idea.pinned || false)
  const [pinning, setPinning] = useState(false)
  const [isRefineModalOpen, setIsRefineModalOpen] = useState(false)
  const [isAuriHelpOpen, setIsAuriHelpOpen] = useState(false)
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)
  const [feedbackTeamMember, setFeedbackTeamMember] = useState(null)
  const [isArchived, setIsArchived] = useState(idea.archived || false)
  const [openTaskCount, setOpenTaskCount] = useState(0)
  const [hoveredAssistant, setHoveredAssistant] = useState(null)
  const [archiving, setArchiving] = useState(false)
  const [skipping, setSkipping] = useState(false)
  const [highlightTaskId, setHighlightTaskId] = useState(null)
  const [isAssessmentProgressExpanded, setIsAssessmentProgressExpanded] = useState(
    window.innerWidth >= 1024 || !currentIdea?.opportunity_assessment
  )
  const [isIdeaDetailsExpanded, setIsIdeaDetailsExpanded] = useState(window.innerWidth >= 1024)
  const [isResearchDetailsExpanded, setIsResearchDetailsExpanded] = useState(window.innerWidth >= 1024)
  const [isIdeaStatusExpanded, setIsIdeaStatusExpanded] = useState(window.innerWidth >= 1024)
  const tasksRef = useRef(null)
  const [isAskOrinOpen, setIsAskOrinOpen] = useState(false)
  const [generatingInsights, setGeneratingInsights] = useState(false)
  // Track which data sources were included in last whiteboard generation
  // This allows regeneration when new evaluation data becomes available
  const [lastGeneratedDataKey, setLastGeneratedDataKey] = useState(null)
  const [orinTaskCount, setOrinTaskCount] = useState(0)

  const toggleResearchSection = (section) => {
    setExpandedResearchSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const togglePinIdea = async () => {
    if (pinning) return

    try {
      setPinning(true)
      console.log('Toggling pin for idea:', currentIdea.id, 'Current state:', isPinned)

      if (isPinned) {
        const result = await apiService.unpinIdea(currentIdea.id)
        console.log('Unpin result:', result)
        setIsPinned(false)
        setCurrentIdea(prev => ({
          ...prev,
          pinned: false
        }))
      } else {
        const result = await apiService.pinIdea(currentIdea.id)
        console.log('Pin result:', result)
        setIsPinned(true)
        setCurrentIdea(prev => ({
          ...prev,
          pinned: true
        }))
      }
      console.log('Pin toggle successful, new state:', !isPinned)
    } catch (error) {
      console.error('Failed to toggle pin status:', error)
    } finally {
      setPinning(false)
    }
  }

  const toggleArchiveIdea = async () => {
    if (archiving) return

    try {
      setArchiving(true)
      console.log('Toggling archive for idea:', currentIdea.id, 'Current state:', isArchived)

      if (isArchived) {
        const result = await apiService.unarchiveIdea(currentIdea.id)
        console.log('Unarchive result:', result)
        setIsArchived(false)
        setCurrentIdea(prev => ({
          ...prev,
          archived: false,
          archived_at: undefined
        }))
      } else {
        const result = await apiService.archiveIdea(currentIdea.id)
        console.log('Archive result:', result)
        setIsArchived(true)
        setCurrentIdea(prev => ({
          ...prev,
          archived: true,
          archived_at: new Date().toISOString()
        }))
      }
      console.log('Archive toggle successful, new state:', !isArchived)
    } catch (error) {
      console.error('Failed to toggle archive status:', error)
    } finally {
      setArchiving(false)
    }
  }
  
  const toggleHistorySection = (section) => {
    setExpandedHistorySections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const openFeedbackForTeamMember = (teamMember) => {
    setFeedbackTeamMember(teamMember)
    setIsFeedbackOpen(true)
  }

  const loadIdeaHistory = useCallback(async () => {
    try {
      const historyData = await apiService.getIdeaHistory(idea.id)
      setHistory(historyData.status_history || [])
    } catch (err) {
      console.error('Failed to load history:', err)
    }
  }, [idea.id])

  const refreshIdeaData = useCallback(async () => {
    // Don't refresh while user is editing or actively using whiteboard
    // Note: tasks tab is ok to refresh - TaskManager manages its own state
    if (isEditing || activeTab === 'whiteboard') return

    try {
      const freshIdea = await apiService.getIdea(idea.id)
      console.log('Fresh idea data received:', freshIdea)

      // If team_members is missing or empty, request Auri to assign them
      if (!freshIdea.team_members || !Array.isArray(freshIdea.team_members) || freshIdea.team_members.length === 0) {
        console.log('No team_members found, requesting Auri to assign team...')
        try {
          const assignResult = await apiService.assignTeamMembers(idea.id)
          console.log('Team assignment result:', assignResult)
          if (assignResult.team_members && assignResult.team_members.length > 0) {
            freshIdea.team_members = assignResult.team_members
          }
        } catch (assignErr) {
          console.error('Failed to assign team members:', assignErr)
          // Continue with no team_members - will show all as fallback
        }
      }

      setCurrentIdea(freshIdea)
    } catch (err) {
      console.error('Failed to refresh idea:', err)
    }
  }, [idea.id, isEditing, activeTab])

  useEffect(() => {
    if (idea.id) {
      refreshIdeaData()
      loadIdeaHistory()

      // Refresh idea data every 10 seconds
      const interval = setInterval(refreshIdeaData, 10000)

      return () => clearInterval(interval)
    }
  }, [idea.id, refreshIdeaData, loadIdeaHistory])

  useEffect(() => {
    if (idea.openTasksTab && idea.highlightTaskId && tasksRef.current) {
      setTimeout(() => {
        tasksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setHighlightTaskId(idea.highlightTaskId)
        setTimeout(() => setHighlightTaskId(null), 1000)
      }, 300)
    }
  }, [])

  // Sync isPinned and isArchived state with currentIdea
  useEffect(() => {
    setIsPinned(currentIdea.pinned || false)
    setIsArchived(currentIdea.archived || false)
  }, [currentIdea.pinned, currentIdea.archived])

  // Load whiteboard data when idea changes
  // isWhiteboardLoaded is derived from whiteboardLoadedForIdeaId === idea.id
  // so it automatically becomes false when idea.id changes (no reset needed)
  useEffect(() => {
    let cancelled = false

    // Clear stale data from previous idea
    setWhiteboardData(null)
    setWhiteboardVersion(0)

    const loadWhiteboardData = async () => {
      try {
        console.log('Loading whiteboard data for idea:', idea.id)
        const whiteboardResponse = await apiService.getWhiteboard(idea.id)

        // Check if this request is still relevant (idea might have changed)
        if (cancelled) {
          console.log('Whiteboard load cancelled - idea changed')
          return
        }

        console.log('Whiteboard data loaded:', whiteboardResponse)
        setWhiteboardData(whiteboardResponse.whiteboard_data)
        setWhiteboardLoadedForIdeaId(idea.id) // Mark as loaded for THIS idea
      } catch (err) {
        if (cancelled) return
        console.error('Failed to load whiteboard data:', err)
        // Mark as loaded even on error so we show empty whiteboard (not infinite spinner)
        setWhiteboardData(null)
        setWhiteboardLoadedForIdeaId(idea.id)
      }
    }

    loadWhiteboardData()

    // Cleanup function to cancel if idea changes before load completes
    return () => {
      cancelled = true
    }
  }, [idea.id])

  // Reset stage to Orin (stage 1) when idea changes
  useEffect(() => {
    resetProgress()
  }, [idea.id, resetProgress])

  // Load task count and refresh TaskManager when assessment completes (tasks are created server-side)
  useEffect(() => {
    const loadTaskCount = async () => {
      try {
        const response = await apiService.getTasks(idea.id)
        const openTasks = (response.tasks || []).filter(task => !task.completed)
        setOpenTaskCount(openTasks.length)
        // Also refresh TaskManager if it exists
        tasksRef.current?.refresh?.()
      } catch (err) {
        console.error('Failed to load task count:', err)
      }
    }
    loadTaskCount()
  }, [idea.id, currentIdea?.opportunity_assessment?.score])

  useEffect(() => {
    const loadReports = async () => {
      if (activeTab === 'reports') {
        setLoadingReports(true)
        try {
          const response = await apiService.getReports(idea.id)
          setReports(response.reports || [])
        } catch (err) {
          console.error('Failed to load reports:', err)
        } finally {
          setLoadingReports(false)
        }
      }
    }
    loadReports()
  }, [idea.id, activeTab])

  // Auto-generate whiteboard when assessment score appears
  // Note: Tasks are now generated server-side by the opportunity-assessment-evaluator Lambda
  useEffect(() => {
    const generateWhiteboard = async () => {
      const score = currentIdea?.opportunity_assessment?.score
      const reasoning = currentIdea?.opportunity_assessment?.reason

      // Create a key representing current available data sources
      const hasUSP = !!currentIdea?.usp_evaluation?.reason
      const hasMarketDemand = !!currentIdea?.market_demand_evaluation?.reason
      const currentDataKey = `${score}-${hasUSP}-${hasMarketDemand}`

      console.log('generateWhiteboard useEffect triggered:', {
        hasScore: !!score,
        hasReasoning: !!reasoning,
        hasUSP,
        hasMarketDemand,
        currentDataKey,
        lastGeneratedDataKey,
        isWhiteboardLoaded,
        hasWhiteboardData: !!whiteboardData
      })

      // Wait until whiteboard data has been loaded from API before deciding to generate
      if (!score || !reasoning || !isWhiteboardLoaded) {
        console.log('generateWhiteboard: early return - missing required data')
        return
      }

      // Skip if we already generated with this exact data configuration
      if (currentDataKey === lastGeneratedDataKey) {
        console.log('generateWhiteboard: already generated with this data, skipping')
        return
      }

      // Check if SWOT pages already exist in loaded whiteboard data
      // This prevents regenerating SWOT on every page reload
      const existingStore = whiteboardData?.store || whiteboardData?.document?.store || {}
      const existingSwotPages = Object.values(existingStore).filter(
        item => item.typeName === 'page' && item.name?.startsWith('SWOT ')
      )
      if (existingSwotPages.length > 0 && lastGeneratedDataKey === null) {
        console.log('generateWhiteboard: SWOT pages already exist, marking as generated:', existingSwotPages.map(p => p.name))
        setLastGeneratedDataKey(currentDataKey)
        return
      }

      // Parse the assessment sections first
      const sections = parseAssessmentSections(reasoning)
      if (!sections) {
        console.log('Failed to parse assessment sections from reasoning')
        return
      }

      // Parse USP evaluation sections if available (from "Find Your Edge" analysis)
      const uspReasoning = currentIdea?.usp_evaluation?.reason
      const uspSections = uspReasoning ? parseUSPSections(uspReasoning) : null
      console.log('generateWhiteboard: parsed USP sections:', uspSections)

      // Parse market demand evaluation sections if available (from "Research Market Demand" analysis)
      const marketDemandReasoning = currentIdea?.market_demand_evaluation?.reason
      const marketDemandSections = marketDemandReasoning ? parseMarketDemandSections(marketDemandReasoning) : null
      console.log('generateWhiteboard: parsed market demand sections:', marketDemandSections)

      setGeneratingInsights(true)
      try {
        console.log('generateWhiteboard: attempting to generate SWOT content')
        console.log('generateWhiteboard: current whiteboardData:', whiteboardData)

        // Generate SWOT - always creates a new page with timestamp
        const swotResult = generateSwotWhiteboard(sections, uspSections, marketDemandSections, whiteboardData)
        if (swotResult) {
          const { newStoreEntries, pageId, pageName } = swotResult
          console.log('SWOT whiteboard generated:', pageName, 'merging with existing data...')

          // Get existing store from either format: {store} or {document: {store}}
          const existingStore = whiteboardData?.store || whiteboardData?.document?.store || {}

          // Merge new SWOT entries into existing store
          const mergedStore = {
            ...existingStore,
            ...newStoreEntries
          }

          // Build merged data in the format tldraw expects
          let mergedData
          if (whiteboardData?.document) {
            // Existing data is in {document, session} format - merge into it
            mergedData = {
              ...whiteboardData,
              document: {
                ...whiteboardData.document,
                store: mergedStore
              }
            }
          } else if (whiteboardData?.store) {
            // Existing data is in {store, schema} format - merge into it
            mergedData = {
              ...whiteboardData,
              store: mergedStore
            }
          } else {
            // No existing data - create fresh {store, schema} format
            const schema = createTLSchema()
            mergedData = {
              store: mergedStore,
              schema: schema.serialize()
            }
          }

          console.log('Merged whiteboard with SWOT data, total store entries:', Object.keys(mergedStore).length)

          setWhiteboardData(mergedData)
          setWhiteboardVersion(v => v + 1) // Force tldraw to remount with new data

          // Save the merged whiteboard to the API
          try {
            await apiService.saveWhiteboard(idea.id, mergedData)
            console.log('Generated whiteboard saved to API')
          } catch (saveErr) {
            console.error('Failed to save generated whiteboard:', saveErr)
          }
        } else {
          console.log('generateSwotWhiteboard returned null (no bullets to generate)')
        }

        // Record which data was used for this generation
        setLastGeneratedDataKey(currentDataKey)
        console.log('generateWhiteboard: recorded data key:', currentDataKey)
      } catch (err) {
        console.error('Failed to generate whiteboard:', err)
      } finally {
        setGeneratingInsights(false)
      }
    }
    generateWhiteboard()
  // Note: whiteboardData intentionally NOT in deps to avoid re-running when we update it
  }, [currentIdea?.opportunity_assessment?.score, currentIdea?.opportunity_assessment?.reason, currentIdea?.usp_evaluation?.reason, currentIdea?.market_demand_evaluation?.reason, lastGeneratedDataKey, isWhiteboardLoaded, idea.id])

  const startPolling = () => {
    apiService.pollIdeaStatus(idea.id, (status) => {
      if (status.error) {
        setError(status.error)
      } else {
        setCurrentIdea(prev => ({ ...prev, ...status }))
      }
    })
  }

  const handleEdit = () => {
    setIsEditing(true)
    setEditForm({
      what: currentIdea.what || '',
      who: currentIdea.who || '',
      why: currentIdea.why || '',
      what_expected: currentIdea.what_expected || '',
      make_money: currentIdea.make_money || false,
      positive_examples: currentIdea.positive_examples || '',
      negative_examples: currentIdea.negative_examples || ''
    })
  }

  const handleSave = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await apiService.updateIdea(idea.id, editForm)
      const updatedIdea = {
        ...currentIdea,
        ...response,
        ...editForm,
        updated_at: new Date().toISOString()
      }
      setCurrentIdea(updatedIdea)
      setIsEditing(false)
      startPolling()
    } catch (err) {
      setError('Failed to update idea. Please try again.')
      console.error('Update error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditForm({
      what: currentIdea.what || '',
      who: currentIdea.who || '',
      why: currentIdea.why || '',
      what_expected: currentIdea.what_expected || '',
      make_money: currentIdea.make_money || false,
      positive_examples: currentIdea.positive_examples || '',
      negative_examples: currentIdea.negative_examples || ''
    })
  }

  const handleWhiteboardSave = async (whiteboardSnapshot) => {
    try {
      await apiService.saveWhiteboard(idea.id, whiteboardSnapshot)
      console.log('Whiteboard saved successfully')
    } catch (err) {
      console.error('Failed to save whiteboard:', err)
    }
  }

  const handleWhiteboardReset = () => {
    // Clear local state so tldraw starts fresh
    setWhiteboardData(null)
    setIsWhiteboardLoaded(false)
    setInsightsGenerated(false)
    console.log('Whiteboard reset - state cleared')
  }

  const handleDownloadReport = async (report) => {
    try {
      const fetchUrl = `${apiService.baseURL}${report.download_url}`
      console.log('Downloading report from:', fetchUrl)
      console.log('Report object:', report)
      
      const response = await fetch(fetchUrl, {
        method: 'GET',
        headers: {
          'Authorization': apiService.getAuthToken(),
        },
      })

      if (!response.ok) {
        throw new Error('Failed to download report')
      }

      // Check if response is base64-encoded text
      const contentType = response.headers.get('Content-Type')
      let blob
      
      if (contentType?.includes('application/json') || contentType?.includes('text/')) {
        // Response might be base64-encoded text
        const text = await response.text()
        try {
          // Try to decode as base64
          const binaryString = atob(text)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
        } catch {
          // Not base64, treat as error
          throw new Error('Invalid response format')
        }
      } else {
        // Response is already binary
        blob = await response.blob()
      }

      const blobUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = report.file_name || 'report.docx'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(blobUrl)
      document.body.removeChild(a)
    } catch (err) {
      console.error('Failed to download report:', err)
      setError('Failed to download report. Please try again.')
    }
  }

  const getStatusConfig = (status) => {
    switch (status) {
      case 'triage':
        return {
          color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
          icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
          label: 'Under Review'
        }
      case 'idea-refinement':
        return {
          color: 'bg-blue-900/30 text-blue-300 border-blue-700',
          icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
          label: 'Needs Refinement'
        }
      case 'opportunity-assessment':
        return {
          color: 'bg-green-500/20 text-green-400 border-green-500/30',
          icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
          label: 'Ready for Assessment'
        }
      case 'search-terms-generated':
        return {
          color: 'bg-purple-900/30 text-purple-300 border-purple-700',
          icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
          label: 'Search Terms Generated'
        }
      case 'web-searches-completed':
        return {
          color: 'bg-indigo-900/30 text-indigo-300 border-indigo-700',
          icon: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0 0V3',
          label: 'Web Searches Completed'
        }
      case 'content-extraction-completed':
        return {
          color: 'bg-emerald-900/30 text-emerald-300 border-emerald-700',
          icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
          label: 'Content Extracted'
        }
      case 'market-demand-evaluated':
        return {
          color: 'bg-rose-900/30 text-rose-300 border-rose-700',
          icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
          label: 'Market Demand Evaluated'
        }
      case 'market-saturation-evaluated':
        return {
          color: 'bg-orange-900/30 text-orange-300 border-orange-700',
          icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z',
          label: 'Market Saturation Evaluated'
        }
      case 'usp-evaluated':
        return {
          color: 'bg-purple-900/30 text-purple-300 border-purple-700',
          icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
          label: 'USP Evaluated'
        }
      case 'opportunity-assessed':
        return {
          color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
          icon: 'M5 3l14 9-14 9V3z',
          label: 'Opportunity Assessed'
        }
      case 'completed':
        return {
          color: 'bg-gray-700/50 text-gray-300 border-gray-600',
          icon: 'M5 13l4 4L19 7',
          label: 'Completed'
        }
      default:
        return {
          color: 'bg-gray-700/50 text-gray-300 border-gray-600',
          icon: 'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
          label: 'Unknown'
        }
    }
  }

  const statusConfig = getStatusConfig(currentIdea.status)

  const formatDate = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Group history entries by time periods
  const groupHistoryByTime = (historyEntries) => {
    const now = new Date()
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

    const groups = {
      thisWeek: [],
      thisMonth: [],
      older: []
    }

    if (!historyEntries || !Array.isArray(historyEntries)) {
      return groups
    }

    historyEntries.forEach(entry => {
      const entryDate = new Date(entry.timestamp)
      
      if (entryDate >= oneWeekAgo) {
        groups.thisWeek.push(entry)
      } else if (entryDate >= oneMonthAgo) {
        groups.thisMonth.push(entry)
      } else {
        groups.older.push(entry)
      }
    })

    return groups
  }

  return (
    <div className="min-h-full">
      <PageHeader
        title="My Idea"
        subtitle={currentIdea.title || currentIdea.what?.substring(0, 80) + (currentIdea.what?.length > 80 ? '...' : '')}
        onCreateIdea={onCreateIdea}
        onCreateIdeaChat={onCreateIdeaChat}
        onLogout={onLogout}
        onHelp={() => setIsAuriHelpOpen(true)}
        onFeedback={() => setIsFeedbackOpen(true)}
        navItems={
          <>
            <button 
              onClick={onBack}
              className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            ← Back to Dashboard
          </button>
          </>
        }
        stats={
          <>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors relative z-[60]"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={togglePinIdea}
              disabled={pinning}
              className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-colors group ${
                pinning ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/10'
              }`}
              title={currentIdea?.pinned ? 'Unpin' : 'Pin'}
            >
              {pinning ? (
                <svg className="w-5 h-5 animate-spin text-gray-300" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg
                  className={`w-5 h-5 transition-colors ${
                    isPinned
                      ? 'text-cyan-400'
                      : 'text-gray-300 group-hover:text-white'
                  }`}
                  fill={isPinned ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                  />
                </svg>
              )}
              <span className={`text-sm font-medium transition-colors ${
                pinning ? 'text-gray-400' : isPinned
                  ? 'text-cyan-400'
                  : 'text-gray-300 group-hover:text-white'
              }`}>
                {pinning ? 'Saving...' : isPinned ? 'Pinned' : 'Pin'}
              </span>
            </button>
            <button
              onClick={toggleArchiveIdea}
              disabled={archiving}
              className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-colors group ${
                archiving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/10'
              }`}
              title={currentIdea?.archived ? 'Unarchive' : 'Archive'}
            >
              {archiving ? (
                <svg className="w-5 h-5 animate-spin text-gray-300" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg
                  className={`w-5 h-5 transition-colors ${
                    isArchived
                      ? 'text-gray-400'
                      : 'text-gray-300 group-hover:text-white'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                  />
                </svg>
              )}
              <span className={`text-sm font-medium transition-colors ${
                archiving ? 'text-gray-400' : isArchived
                  ? 'text-gray-400'
                  : 'text-gray-300 group-hover:text-white'
              }`}>
                {archiving ? 'Saving...' : isArchived ? 'Unarchive' : 'Archive'}
              </span>
            </button>
          </div>
          </>
        }
      />

      <main className="-mt-20 md:-mt-24 pb-8">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-40 bg-black/20"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed top-40 right-4 z-50 w-64 rounded-2xl overflow-hidden bg-gradient-to-br from-gray-700/80 to-gray-700/80 shadow-none outline -outline-offset-1 outline-white/10">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none z-10"></div>
          <button
            onClick={() => {
              setActiveTab('details')
              setIsMobileMenuOpen(false)
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
              activeTab === 'details'
                ? 'bg-white/10 text-white'
                : 'text-gray-300 hover:bg-white/5'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="font-medium">Idea Details &amp; Progress</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('whiteboard')
              setIsMobileMenuOpen(false)
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
              activeTab === 'whiteboard'
                ? 'bg-white/10 text-white'
                : 'text-gray-300 hover:bg-white/5'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            <span className="font-medium">Ideation Board</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('tasks')
              setIsMobileMenuOpen(false)
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
              activeTab === 'tasks'
                ? 'bg-white/10 text-white'
                : 'text-gray-300 hover:bg-white/5'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <span className="font-medium">Tasks</span>
            {openTaskCount > 0 && (
              <span className="ml-auto px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-500 text-white">
                {openTaskCount}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setActiveTab('reports')
              setIsMobileMenuOpen(false)
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
              activeTab === 'reports'
                ? 'bg-white/10 text-white'
                : 'text-gray-300 hover:bg-white/5'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="font-medium">Reports</span>
          </button>

          <div className="border-t border-gray-600 mt-3 pt-3">
            <p className="px-4 pb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</p>
            <button
              onClick={() => {
                togglePinIdea()
                setIsMobileMenuOpen(false)
              }}
              disabled={pinning}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                pinning ? 'opacity-50 cursor-not-allowed text-gray-500' : 'text-gray-300 hover:bg-white/5'
              }`}
            >
              {pinning ? (
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg 
                  className={isPinned ? 'w-5 h-5 text-cyan-400' : 'w-5 h-5'} 
                  fill={isPinned ? 'currentColor' : 'none'} 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              )}
              <span className="font-medium">{pinning ? 'Saving...' : isPinned ? 'Unpin' : 'Pin'}</span>
            </button>
            
            <button
              onClick={() => {
                toggleArchiveIdea()
                setIsMobileMenuOpen(false)
              }}
              disabled={archiving}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                archiving ? 'opacity-50 cursor-not-allowed text-gray-500' : isArchived ? 'text-gray-500' : 'text-gray-300 hover:bg-white/5'
              }`}
            >
              {archiving ? (
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              )}
              <span className="font-medium">{archiving ? 'Saving...' : isArchived ? 'Unarchive' : 'Archive'}</span>
            </button>
          </div>

          <div className="border-t border-gray-600 mt-3 pt-3">
            <p className="px-4 pb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">General</p>
            <button
              onClick={() => {
                onCreateIdea()
                setIsMobileMenuOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-300 hover:bg-white/5 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="font-medium">New Idea</span>
            </button>

            <button
              onClick={() => {
                setIsAuriHelpOpen(true)
                setIsMobileMenuOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-300 hover:bg-white/5 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
              </svg>
              <span className="font-medium">Help</span>
            </button>

            <button
              onClick={() => {
                setIsFeedbackOpen(true)
                setIsMobileMenuOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-300 hover:bg-white/5 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
              </svg>
              <span className="font-medium">Feedback</span>
            </button>

            <button
              onClick={() => {
                onLogout()
                setIsMobileMenuOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-300 hover:bg-white/5 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="pb-8 relative z-20">
        <div className="hidden md:flex items-end justify-between gap-4">
          {/* Tabs */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveTab('details')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium text-sm relative overflow-hidden ${
              activeTab === 'details'
                ? 'bg-white/5 text-white before:absolute before:top-0 before:left-0 before:right-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent after:absolute after:top-0 after:left-0 after:w-32 after:h-32 after:bg-gradient-to-br after:from-white/10 after:via-transparent after:to-transparent after:rounded-tl-lg after:pointer-events-none'
                : 'text-gray-300 bg-white/[0.02] hover:bg-white/10 hover:text-white'
            }`}
          >
            <svg className="w-4 h-4 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="relative z-10">Idea Details &amp; Progress</span>
          </button>
          <button
            onClick={() => setActiveTab('whiteboard')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium text-sm relative overflow-hidden ${
              activeTab === 'whiteboard'
                ? 'bg-white/5 text-white before:absolute before:top-0 before:left-0 before:right-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent after:absolute after:top-0 after:left-0 after:w-32 after:h-32 after:bg-gradient-to-br after:from-white/10 after:via-transparent after:to-transparent after:rounded-tl-lg after:pointer-events-none'
                : 'text-gray-300 bg-white/[0.02] hover:bg-white/10 hover:text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            <span className="relative z-10">Ideation Board</span>
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium text-sm relative overflow-hidden ${
              activeTab === 'tasks'
                ? 'bg-white/5 text-white before:absolute before:top-0 before:left-0 before:right-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent after:absolute after:top-0 after:left-0 after:w-32 after:h-32 after:bg-gradient-to-br after:from-white/10 after:via-transparent after:to-transparent after:rounded-tl-lg after:pointer-events-none'
                : 'text-gray-300 bg-white/[0.02] hover:bg-white/10 hover:text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <span className="relative z-10">Tasks</span>
            {openTaskCount > 0 && (
              <span className="relative z-10 ml-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-500 text-white">
                {openTaskCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium text-sm relative overflow-hidden ${
              activeTab === 'reports'
                ? 'bg-white/5 text-white before:absolute before:top-0 before:left-0 before:right-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent after:absolute after:top-0 after:left-0 after:w-32 after:h-32 after:bg-gradient-to-br after:from-white/10 after:via-transparent after:to-transparent after:rounded-tl-lg after:pointer-events-none'
                : 'text-gray-300 bg-white/[0.02] hover:bg-white/10 hover:text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="relative z-10">Reports</span>
          </button>
          </div>
          {/* Auri helper panel - aligned with tabs */}
          <button
            onClick={() => setIsAuriHelpOpen(true)}
            className="bg-gradient-to-r from-purple-900/40 to-indigo-900/30 rounded-xl p-4 border border-purple-500/30 hover:border-purple-400 transition-all group -mt-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex-shrink-0 rounded-full overflow-hidden bg-purple-500/20">
                <img src={`${import.meta.env.BASE_URL}characters/Auri/1_Default/folded2.png`} alt="Auri" className="w-full h-full object-cover scale-125" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm text-purple-300 font-medium">Auri - Your Guide</p>
                <p className="text-base text-gray-300 group-hover:text-white transition-colors">Questions? I&apos;m here to help!</p>
              </div>
              <span className="text-sm bg-purple-500 group-hover:bg-purple-600 text-white px-4 py-2 rounded-full transition-colors whitespace-nowrap">
                Ask
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* Stage Navigation */}
      {activeTab === 'details' && (
        <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-6 py-8 sm:px-8 mt-4 mb-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
          <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-blue-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
          <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-blue-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>

          <div className="relative z-10 space-y-6">
            {/* Team header */}
            <div className="text-center py-4">
              <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-white">Your Idea&apos;s Team &amp; Their Progress</h2>
            </div>

            {/* Team with overlapping images */}
            {currentIdea?.team_members === undefined ? (
              // Loading spinner while team data is being fetched
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-12 h-12 border-4 border-gray-600 border-t-sky-500 rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-400 text-sm">Loading team...</p>
              </div>
            ) : (
            <>
            {/* Mobile: Active Focus with Summary */}
            <div className="md:hidden space-y-3">
              {(() => {
                const allMembers = [ASSISTANTS.orin, ASSISTANTS.cleo, ASSISTANTS.finn, ASSISTANTS.una, ASSISTANTS.vera]
                const filteredMembers = allMembers.filter(member => {
                  if (!currentIdea) return false
                  if (Array.isArray(currentIdea.team_members) && currentIdea.team_members.length > 0) {
                    return currentIdea.team_members.some(name => name.toLowerCase() === member.id)
                  }
                  return true
                })

                const completedMembers = filteredMembers.filter(member => {
                  const memberIndex = allMembers.findIndex(m => m.id === member.id)
                  return getStageStatus(memberIndex + 1, currentStage, currentIdea) === 'completed'
                })
                const activeMember = filteredMembers.find(member => {
                  const memberIndex = allMembers.findIndex(m => m.id === member.id)
                  return getStageStatus(memberIndex + 1, currentStage, currentIdea) === 'current'
                })
                const pendingMembers = filteredMembers.filter(member => {
                  const memberIndex = allMembers.findIndex(m => m.id === member.id)
                  return getStageStatus(memberIndex + 1, currentStage, currentIdea) === 'locked'
                })

                // If no active member, show the last completed member as prominent
                const prominentMember = activeMember || completedMembers[completedMembers.length - 1]
                const isProminentCompleted = !activeMember && prominentMember
                // Completed members to show in summary (exclude the one shown prominently)
                const summaryCompletedMembers = isProminentCompleted
                  ? completedMembers.slice(0, -1)
                  : completedMembers

                return (
                  <>
                    {/* Completed - Compact row (only if there are completed members not shown prominently) */}
                    {summaryCompletedMembers.length > 0 && (
                      <div className="flex items-center gap-2 px-1">
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex -space-x-2">
                          {summaryCompletedMembers.map(member => (
                            <button
                              key={member.id}
                              onClick={() => goToStage(allMembers.findIndex(m => m.id === member.id) + 1)}
                              className="w-8 h-8 rounded-full border-2 border-green-500 overflow-hidden bg-gray-800 hover:scale-110 transition-transform"
                            >
                              <img src={member.poses.default} alt={member.name} className="w-full h-full object-cover object-top scale-150 translate-y-1" />
                            </button>
                          ))}
                        </div>
                        <span className="text-green-400 text-xs font-medium">
                          {summaryCompletedMembers.length === 1 ? `${summaryCompletedMembers[0].name} done` : `${summaryCompletedMembers.length} completed`}
                        </span>
                      </div>
                    )}

                    {/* Prominent Member - Active or Last Completed */}
                    {prominentMember && (
                      <button
                        onClick={() => goToStage(allMembers.findIndex(m => m.id === prominentMember.id) + 1)}
                        className={`w-full rounded-xl p-4 ring-2 shadow-lg text-left ${
                          isProminentCompleted
                            ? 'bg-gradient-to-r from-green-900/80 to-green-800/60 ring-green-500 shadow-green-500/20'
                            : 'bg-gradient-to-r from-sky-900/80 to-sky-800/60 ring-sky-500 shadow-sky-500/20'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className={`w-16 h-16 rounded-full overflow-hidden bg-gray-800 ring-2 ${
                              isProminentCompleted ? 'ring-green-400' : 'ring-sky-400'
                            }`}>
                              <img src={prominentMember.poses.default} alt={prominentMember.name} className="w-full h-full object-cover object-top scale-150 translate-y-2" />
                            </div>
                            <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ${
                              isProminentCompleted ? 'bg-green-500' : 'bg-sky-500'
                            }`}>
                              {isProminentCompleted ? (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-3 h-3 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                              )}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className={`text-xs font-medium mb-0.5 ${
                              isProminentCompleted ? 'text-green-300' : 'text-sky-300'
                            }`}>
                              {isProminentCompleted ? 'Completed' : 'Currently Working'}
                            </div>
                            <h3 className="text-white font-bold text-lg">{prominentMember.name}</h3>
                            <p className={`text-sm ${isProminentCompleted ? 'text-green-200' : 'text-sky-200'}`}>{prominentMember.title}</p>
                          </div>
                        </div>
                      </button>
                    )}

                    {/* Pending - Future members */}
                    {pendingMembers.length > 0 && (
                      <div className="flex items-center gap-3 px-1">
                        <div className="flex -space-x-3">
                          {pendingMembers.slice(0, 4).map(member => (
                            <button
                              key={member.id}
                              onClick={() => goToStage(allMembers.findIndex(m => m.id === member.id) + 1)}
                              className="w-12 h-12 rounded-full border-2 border-gray-400 overflow-hidden bg-gray-800 grayscale brightness-110 hover:scale-110 hover:brightness-125 transition-transform"
                            >
                              <img src={member.poses.default} alt={member.name} className="w-full h-full object-cover object-top scale-150 translate-y-1" />
                            </button>
                          ))}
                        </div>
                        <span className="text-gray-200 text-xs">
                          Upcoming team members
                        </span>
                      </div>
                    )}
                  </>
                )
              })()}
            </div>

            {/* Desktop: Original grid layout */}
            <div className={`hidden md:grid gap-4 items-stretch pt-16 ${
              (() => {
                const teamCount = currentIdea?.team_members?.length || 5
                return teamCount <= 3 ? 'grid-cols-3' : teamCount === 4 ? 'grid-cols-4' : 'grid-cols-5'
              })()
            }`}>
              {[ASSISTANTS.orin, ASSISTANTS.cleo, ASSISTANTS.finn, ASSISTANTS.una, ASSISTANTS.vera]
                .filter(member => {
                  if (!currentIdea) {
                    return false
                  }
                  // If team_members has values, filter by those
                  if (Array.isArray(currentIdea.team_members) && currentIdea.team_members.length > 0) {
                    return currentIdea.team_members.some(name => name.toLowerCase() === member.id)
                  }
                  // Fallback: show all if team_members is empty array or null
                  return true
                })
                .map((member, index) => {
                  const memberIndex = [ASSISTANTS.orin, ASSISTANTS.cleo, ASSISTANTS.finn, ASSISTANTS.una, ASSISTANTS.vera].findIndex(m => m.id === member.id)
                  const stageId = memberIndex + 1
                  const status = getStageStatus(stageId, currentStage, currentIdea)
                  const isCompleted = status === 'completed'
                  const isWorkingOnThis = status === 'current' // Actually working on this stage
                  const isPending = status === 'locked' // Not yet started
                  const isSelected = currentStage === stageId // Currently viewing this stage

                  return (
                    <button
                      key={member.id}
                      onClick={() => goToStage(stageId)}
                      className={`relative rounded-2xl pt-14 pb-4 px-3 transition-all text-center cursor-pointer flex flex-col items-center ${
                        isCompleted
                          ? 'bg-gradient-to-b from-green-800/30 to-gray-800/30 ring-1 ring-green-500/50 hover:ring-green-400'
                          : isWorkingOnThis
                          ? 'bg-sky-900/60 ring-2 ring-sky-500 shadow-xl shadow-sky-500/30'
                          : isSelected && isPending
                          ? 'bg-gray-600/50 ring-1 ring-gray-400/50'
                          : 'bg-gray-800/40 ring-1 ring-gray-700/50 hover:ring-gray-600'
                      }`}
                    >
                      {/* Overlapping avatar - fixed top position */}
                      <div
                        className={`absolute left-1/2 -translate-x-1/2 w-32 h-32 ${isPending ? 'opacity-70 grayscale' : ''}`}
                        style={{ top: '-80px' }}
                      >
                        <img
                          src={member.poses.default}
                          alt={member.name}
                          className="w-full h-full object-contain object-bottom drop-shadow-lg"
                        />
                      </div>

                      {/* Status badge in top right corner */}
                      {isCompleted && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      {isWorkingOnThis && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-sky-500 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        </div>
                      )}
                      {isPending && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center ring-1 ring-gray-900">
                          <svg className="w-3 h-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                      )}

                      {/* Name */}
                      <h3 className={`font-bold text-base mb-0.5 ${
                        isCompleted ? 'text-green-300' : isWorkingOnThis ? 'text-white' : isSelected && isPending ? 'text-white' : 'text-gray-200 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]'
                      }`}>
                        {member.name}
                      </h3>

                      {/* Title */}
                      <p className={`text-xs font-medium mb-2 ${
                        isCompleted ? 'text-green-400' : isWorkingOnThis ? 'text-sky-300' : isSelected && isPending ? 'text-gray-300' : 'text-gray-300 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]'
                      }`}>
                        {member.title}
                      </p>

                      {/* Step label - below title */}
                      <div className="mb-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                          isCompleted
                            ? 'bg-green-500/20 text-green-400'
                            : isWorkingOnThis
                            ? 'bg-sky-500/30 text-sky-300'
                            : isSelected && isPending
                            ? 'bg-gray-500/40 text-gray-200'
                            : 'bg-gray-500/60 text-gray-200 drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]'
                        }`}>
                          STEP {index + 1}
                        </span>
                      </div>

                      {/* Description */}
                      <p className={`text-xs leading-relaxed ${
                        isCompleted ? 'text-gray-400' : isWorkingOnThis ? 'text-gray-300' : isSelected && isPending ? 'text-gray-300' : 'text-gray-400 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]'
                      }`}>
                        {member.subtitle}
                      </p>

                      {/* Learn more indicator for inactive cards (hide when selected) */}
                      {isPending && !isSelected && (
                        <p className="text-xs text-purple-400 mt-auto pt-3 flex items-center justify-center gap-1.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Click to learn more
                        </p>
                      )}
                    </button>
                  )
                })}
            </div>
            </>
            )}
          </div>
        </div>
      )}

      {/* Stage Title */}
      {activeTab === 'details' && (
        <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-6 py-8 sm:px-8 mb-12 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
          <div className={`absolute top-0 left-0 w-96 h-40 bg-gradient-to-br ${currentStage === 1 ? 'from-cyan-900/20' : 'from-gray-700/20'} via-transparent to-transparent rounded-tl-2xl pointer-events-none`}></div>
          <div className={`absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl ${currentStage === 1 ? 'from-cyan-900/20' : 'from-gray-700/20'} via-transparent to-transparent rounded-br-2xl pointer-events-none`}></div>
          <div className="relative z-10">


            <div className="flex flex-col lg:grid lg:grid-cols-5 gap-0 lg:gap-8">
              <div className="hidden md:block lg:col-span-2 space-y-8 overflow-visible order-1 lg:order-2">
                {/* Commercial Ideation Progress - Stage 2 */}
                {currentStage === 2 && !isEditing && (
                  <div className="rounded-2xl bg-gradient-to-br from-gray-700/60 to-gray-700/60 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                    <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-gray-700/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                    <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-gray-700/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>

                    {/* Header */}
                    <div className="relative z-10">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <h3 className="font-display text-2xl md:text-2xl font-light tracking-tight text-white">My Progress</h3>
                          <p className="text-sm text-gray-300">Let me show you how we'll conquer this market...</p>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-6">
                        <div className="flex justify-between text-base mb-2">
                          <span className="font-medium text-gray-200">Overall Progress</span>
                          <span className="text-gray-300 font-medium">Step 0 of 4</span>
                        </div>
                        <div className="w-full bg-gray-600 rounded-full h-2">
                          <div className="bg-gradient-to-r from-gray-500 to-gray-600 h-2 rounded-full transition-all duration-700" 
                               style={{ width: '0%' }}></div>
                        </div>
                      </div>

                      {/* Steps */}
                      <div>
                        {/* Step 1: Market Positioning */}
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">
                                First, I identify your perfect customer
                              </h4>
                              <p className="text-sm text-gray-400 mb-1">I pinpoint exactly who needs this and why they'll love it</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>

                        {/* Step 2: Go-to-Market Strategy */}
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">
                                Next, I craft your launch strategy
                              </h4>
                              <p className="text-sm text-gray-400 mb-1">I map out channels, pricing, and how to make a splash</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>

                        {/* Step 3: Revenue Model */}
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">
                                Then, I design your money machine
                              </h4>
                              <p className="text-sm text-gray-400 mb-1">I figure out how this idea turns into real revenue</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>

                        {/* Step 4: Competitive Analysis */}
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">
                                Finally, I position you to win
                              </h4>
                              <p className="text-sm text-gray-400 mb-1">I find your competitive edge and make it impossible to ignore</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Business Case Progress - Stage 3 */}
                {currentStage === 3 && !isEditing && (
                  <div className="rounded-2xl bg-gradient-to-br from-gray-700/60 to-gray-700/60 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                    <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-gray-700/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                    <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-gray-700/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>

                    <div className="relative z-10">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <h3 className="font-display text-2xl md:text-2xl font-light tracking-tight text-white">My Progress</h3>
                          <p className="text-sm text-gray-300">I'll turn all this research into something you can share...</p>
                        </div>
                      </div>

                      <div className="mb-6">
                        <div className="flex justify-between text-base mb-2">
                          <span className="font-medium text-gray-200">Overall Progress</span>
                          <span className="text-gray-300 font-medium">Step 0 of 4</span>
                        </div>
                        <div className="w-full bg-gray-600 rounded-full h-2">
                          <div className="bg-gradient-to-r from-gray-500 to-gray-600 h-2 rounded-full transition-all duration-700" 
                               style={{ width: '0%' }}></div>
                        </div>
                      </div>

                      <div>
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">First, I synthesize the insights</h4>
                              <p className="text-sm text-gray-400 mb-1">I pull together everything Orin and Cleo discovered into one clear narrative</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">Next, I build the financial story</h4>
                              <p className="text-sm text-gray-400 mb-1">I create projections and investment requirements that make sense</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">Then, I address the risks</h4>
                              <p className="text-sm text-gray-400 mb-1">I identify what could go wrong and how we'll handle it</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">Finally, I package it for sharing</h4>
                              <p className="text-sm text-gray-400 mb-1">I create a business case you can present to stakeholders and investors</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* UX Design Progress - Stage 4 */}
                {currentStage === 4 && !isEditing && (
                  <div className="rounded-2xl bg-gradient-to-br from-gray-700/60 to-gray-700/60 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                    <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-gray-700/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                    <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-gray-700/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>

                    <div className="relative z-10">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <h3 className="font-display text-2xl md:text-2xl font-light tracking-tight text-white">My Progress</h3>
                          <p className="text-sm text-gray-300">I'll make sure users fall in love with this...</p>
                        </div>
                      </div>

                      <div className="mb-6">
                        <div className="flex justify-between text-base mb-2">
                          <span className="font-medium text-gray-200">Overall Progress</span>
                          <span className="text-gray-300 font-medium">Step 0 of 4</span>
                        </div>
                        <div className="w-full bg-gray-600 rounded-full h-2">
                          <div className="bg-gradient-to-r from-gray-500 to-gray-600 h-2 rounded-full transition-all duration-700" 
                               style={{ width: '0%' }}></div>
                        </div>
                      </div>

                      <div>
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">First, I get to know your users</h4>
                              <p className="text-sm text-gray-400 mb-1">I discover what they need, how they think, and what frustrates them</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">Next, I sketch out the experience</h4>
                              <p className="text-sm text-gray-400 mb-1">I create prototypes we can test and refine together</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">Then, I build your visual language</h4>
                              <p className="text-sm text-gray-400 mb-1">I craft a design system that feels cohesive and delightful</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">Finally, I validate with real people</h4>
                              <p className="text-sm text-gray-400 mb-1">I test with actual users and iterate until it feels just right</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* MVP Development Progress - Stage 5 */}
                {currentStage === 5 && !isEditing && (
                  <div className="rounded-2xl bg-gradient-to-br from-gray-700/60 to-gray-700/60 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                    <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-gray-700/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
                    <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-gray-700/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>

                    <div className="relative z-10">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <h3 className="font-display text-2xl md:text-2xl font-light tracking-tight text-white">My Progress</h3>
                          <p className="text-sm text-gray-300">Time to build something real...</p>
                        </div>
                      </div>

                      <div className="mb-6">
                        <div className="flex justify-between text-base mb-2">
                          <span className="font-medium text-gray-200">Overall Progress</span>
                          <span className="text-gray-300 font-medium">Step 0 of 4</span>
                        </div>
                        <div className="w-full bg-gray-600 rounded-full h-2">
                          <div className="bg-gradient-to-r from-gray-500 to-gray-600 h-2 rounded-full transition-all duration-700" 
                               style={{ width: '0%' }}></div>
                        </div>
                      </div>

                      <div>
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">First, I architect the foundation</h4>
                              <p className="text-sm text-gray-400 mb-1">I design a system that scales and stays maintainable</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">Next, I build the core features</h4>
                              <p className="text-sm text-gray-400 mb-1">I develop what matters most for your MVP launch</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">Then, I test everything thoroughly</h4>
                              <p className="text-sm text-gray-400 mb-1">I make sure it works reliably before anyone sees it</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex items-center p-3 rounded-r-lg border-l-4 transition-all duration-300 relative overflow-hidden bg-gray-800/30 border-gray-600 opacity-60 cursor-default">
                            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 relative z-10 bg-gray-600">
                              <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                            </div>
                            <div className="flex-1 relative z-10">
                              <h4 className="font-display text-lg md:text-xl font-light tracking-tight text-white">Finally, I ship it to the world</h4>
                              <p className="text-sm text-gray-400 mb-1">I deploy and get it into the hands of real users</p>
                              <p className="text-base text-gray-400" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Assessment Progress - Stage 1 */}
                {currentStage === 1 && !isEditing && (
                  <div>
                  <IdeaProgress
                    currentIdea={currentIdea}
                    formatDate={formatDate}
                    isExpanded={isAssessmentProgressExpanded}
                    onToggle={() => setIsAssessmentProgressExpanded(!isAssessmentProgressExpanded)}
                    onRefineIdea={() => setIsRefineModalOpen(true)}
                    onArchiveIdea={toggleArchiveIdea}
                    archiving={archiving}
                    onProgressToMarketResearch={async () => {
                      try {
                        setSkipping(true)
                        await apiService.forceProgressIdea(currentIdea.id)
                        const refreshedIdea = await apiService.getIdea(currentIdea.id)
                        setCurrentIdea(refreshedIdea)
                        const historyData = await apiService.getIdeaHistory(currentIdea.id)
                        setHistory(historyData.status_history || [])
                      } catch (error) {
                        console.error('Failed to progress idea:', error)
                      } finally {
                        setSkipping(false)
                      }
                    }}
                    skipping={skipping}
                    history={history} />
                  </div>
                )}



          {/* Research Details */}
          {(currentIdea.search_terms || currentIdea.search_results_metadata || currentIdea.content_extraction_metadata) && !isEditing && (
            <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
              <div className={`absolute top-0 left-0 w-96 h-40 bg-gradient-to-br ${currentStage === 1 ? 'from-cyan-900/20' : 'from-gray-700/20'} via-transparent to-transparent rounded-tl-2xl pointer-events-none`}></div>
              <div className={`absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl ${currentStage === 1 ? 'from-cyan-900/20' : 'from-gray-700/20'} via-transparent to-transparent rounded-br-2xl pointer-events-none`}></div>
              <div className="relative z-10">
              <button
                onClick={() => setIsResearchDetailsExpanded(!isResearchDetailsExpanded)}
                className={`w-full lg:pointer-events-none flex items-center justify-between gap-3 text-white lg:hover:opacity-100 hover:opacity-80 transition-opacity ${isResearchDetailsExpanded ? 'mb-6' : 'mb-0'}`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 bg-gradient-to-br ${currentStage === 1 ? 'from-cyan-500 to-cyan-600' : 'from-gray-500 to-gray-600'} rounded-xl flex items-center justify-center shadow-lg`}>
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="font-display text-2xl md:text-2xl font-light tracking-tight text-white">Research Details</h3>
                    <p className="text-gray-300 text-sm">Market research findings and analysis</p>
                  </div>
                </div>
                <svg className={`lg:hidden w-6 h-6 transition-transform ${isResearchDetailsExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isResearchDetailsExpanded && (
              <>
              <div className="space-y-4">
                {/* Market Research Terms Section */}
                {currentIdea.search_terms && (
                  <div className="border border-gray-700 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleResearchSection('marketTerms')}
                      className="w-full px-4 py-3 bg-gray-800/50 hover:bg-gray-800/70 transition-colors flex items-center justify-between text-left"
                    >
                      <div className="flex items-center space-x-2">
                        <svg className={`w-5 h-5 ${currentStage === 1 ? 'text-cyan-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <span className="font-display text-base md:text-xl font-light tracking-tight text-white">Market Research Terms</span>
                      </div>
                      <svg 
                        className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expandedResearchSections.marketTerms ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedResearchSections.marketTerms && (
                      <div className="p-4 bg-gray-800/50 border-t border-gray-700">
                        {/* Category Summary */}
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-300 mb-2 uppercase tracking-wide">Categories</h4>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(currentIdea.search_terms.categories || {}).map(([category, count]) => (
                              <span key={category} className="px-2 py-1 bg-purple-900/30 text-purple-300 border border-purple-700 rounded-full text-sm font-medium">
                                {category.replaceAll('_', ' ')}: {count}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Search Terms List */}
                        <div className="max-h-96 overflow-y-auto">
                          <h4 className="text-sm font-semibold text-gray-300 mb-2 uppercase tracking-wide">Search Terms</h4>
                          <div className="space-y-2">
                            {(currentIdea.search_terms.search_terms || []).map((term) => (
                              <div key={term.term} className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                                <div className="flex items-start justify-between mb-1">
                                  <span className="text-base font-medium text-white">{term.term}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${
                                    term.priority === 'high' 
                                      ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                                      : term.priority === 'medium' 
                                      ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' 
                                      : 'bg-green-500/20 text-green-400 border border-green-500/30'
                                  }`}>
                                    {term.priority}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-400 mb-1">{term.rationale}</p>
                                <span className={`text-sm font-medium ${currentStage === 1 ? 'text-cyan-400' : 'text-gray-400'}`}>
                                  {term.category.replaceAll('_', ' ')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Web Search Results Section */}
                {currentIdea.search_results_metadata && (
                  <div className="border border-gray-700 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleResearchSection('webSearch')}
                      className="w-full px-4 py-3 bg-gray-800/50 hover:bg-gray-800/70 transition-colors flex items-center justify-between text-left"
                    >
                      <div className="flex items-center space-x-2">
                        <svg className={`w-5 h-5 ${currentStage === 1 ? 'text-cyan-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0 0V3" />
                        </svg>
                        <span className="font-display text-base md:text-xl font-light tracking-tight text-white">Web Search Results</span>
                      </div>
                      <svg 
                        className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expandedResearchSections.webSearch ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedResearchSections.webSearch && (
                      <div className="p-4 bg-gray-800/50 border-t border-gray-700">
                        {/* Search Statistics */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                            <div className={`text-xl md:text-2xl font-bold ${currentStage === 1 ? 'text-cyan-600 dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`}>
                              {currentIdea.search_results_metadata.total_search_terms}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Total Terms</div>
                          </div>
                          <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                            <div className="text-xl md:text-2xl font-bold text-green-600 dark:text-green-400">
                              {currentIdea.search_results_metadata.successful_searches}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Successful</div>
                          </div>
                          <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                            <div className="text-xl md:text-2xl font-bold text-red-600 dark:text-red-400">
                              {currentIdea.search_results_metadata.failed_searches}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Failed</div>
                          </div>
                          <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                            <div className={`text-xl md:text-2xl font-bold ${currentStage === 1 ? 'text-blue-600 dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`}>
                              {Math.round(currentIdea.search_results_metadata.total_content_size_bytes / 1024 / 1024 * 10) / 10}MB
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Content Size</div>
                          </div>
                        </div>

                        {/* Search Categories */}
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2 uppercase tracking-wide">Research Categories</h4>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(currentIdea.search_results_metadata.search_categories || {}).map(([category, count]) => (
                              <span key={category} className={`px-2 py-1 rounded-full text-sm font-medium ${currentStage === 1 ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-700' : 'bg-gray-100 dark:bg-gray-700/30 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600'}`}>
                                {category.replaceAll('_', ' ')}: {count} searches
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className={`flex items-center justify-between text-sm ${currentStage === 1 ? 'text-cyan-600 dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          <span>Execution ID: {currentIdea.search_results_metadata.execution_id}</span>
                          <span>Duration: {currentIdea.search_results_metadata.execution_duration}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Content Extraction Results Section */}
                {currentIdea.content_extraction_metadata && (
                  <div className="border border-gray-700 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleResearchSection('contentExtraction')}
                      className="w-full px-4 py-3 bg-gray-800/50 hover:bg-gray-800/70 transition-colors flex items-center justify-between text-left"
                    >
                      <div className="flex items-center space-x-2">
                        <svg className={`w-5 h-5 ${currentStage === 1 ? 'text-cyan-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="font-display text-base md:text-xl font-light tracking-tight text-white">Content Extraction Results</span>
                      </div>
                      <svg 
                        className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expandedResearchSections.contentExtraction ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedResearchSections.contentExtraction && (
                      <div className="p-4 bg-gray-800/50 border-t border-gray-700">
                        {/* Stats Overview */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                            <div className={`text-xl md:text-2xl font-bold ${currentStage === 1 ? 'text-blue-600 dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`}>
                              {currentIdea.content_extraction_metadata?.results?.stats?.total_urls_processed || 0}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">URLs Processed</div>
                          </div>
                          <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                            <div className="text-xl md:text-2xl font-bold text-green-600 dark:text-green-400">
                              {currentIdea.content_extraction_metadata?.results?.stats?.successful_extractions || 0}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Successful</div>
                          </div>
                          <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                            <div className={`text-xl md:text-2xl font-bold ${currentStage === 1 ? 'text-purple-600 dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`}>
                              {currentIdea.content_extraction_metadata?.results?.stats?.content_size_mb || 0}MB
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Content Size</div>
                          </div>
                          <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                            <div className={`text-xl md:text-2xl font-bold ${currentStage === 1 ? 'text-amber-600 dark:text-cyan-400' : 'text-gray-500 dark:text-gray-400'}`}>
                              {currentIdea.content_extraction_metadata?.results?.stats?.success_rate_percent || 0}%
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Success Rate</div>
                          </div>
                        </div>

                        {/* Processing Info */}
                        <div className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center justify-between">
                          <span>Unique Sources: {currentIdea.content_extraction_metadata?.results?.stats?.unique_hosts || 0}</span>
                          <span>Processing Time: {currentIdea.content_extraction_metadata?.results?.processing_time || 0}s</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              </>
              )}
              </div>
            </div>
          )}


          {/* Idea Status - Hidden on mobile, shown on desktop */}
          <div className="hidden md:block rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
            <div className={`absolute top-0 left-0 w-96 h-40 bg-gradient-to-br ${currentStage === 1 ? 'from-cyan-900/20' : 'from-gray-700/20'} via-transparent to-transparent rounded-tl-2xl pointer-events-none`}></div>
            <div className={`absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl ${currentStage === 1 ? 'from-cyan-900/20' : 'from-gray-700/20'} via-transparent to-transparent rounded-br-2xl pointer-events-none`}></div>
            <div className="relative z-10">
            <button
              onClick={() => setIsIdeaStatusExpanded(!isIdeaStatusExpanded)}
              className={`w-full lg:pointer-events-none flex items-center justify-between gap-3 text-white lg:hover:opacity-100 hover:opacity-80 transition-opacity ${isIdeaStatusExpanded ? 'mb-6' : 'mb-0'}`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 bg-gradient-to-br ${currentStage === 1 ? 'from-cyan-500 to-cyan-600' : 'from-gray-500 to-gray-600'} rounded-xl flex items-center justify-center shadow-lg`}>
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <h3 className="font-display text-2xl md:text-2xl font-light tracking-tight text-white">Idea Status</h3>
                  <p className="text-gray-300 text-sm">Current status and progress tracking</p>
                </div>
              </div>
              <svg className={`lg:hidden w-6 h-6 transition-transform ${isIdeaStatusExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isIdeaStatusExpanded && (
            <>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-base text-gray-400">Created</span>
                <span className="text-base font-medium text-gray-100">{formatDate(currentIdea.created_at)}</span>
              </div>
              {currentIdea.updated_at && (
                <div className="flex justify-between items-center">
                  <span className="text-base text-gray-600 dark:text-gray-400">Last Updated</span>
                  <span className="text-base font-medium text-gray-100">{formatDate(currentIdea.updated_at)}</span>
                </div>
              )}
              {/* Status History */}
              <div className="border-t border-gray-200/50 dark:border-gray-700/50 pt-6 mt-6">
                <h4 className="font-display text-xl font-light tracking-tight text-white mb-4 flex items-center space-x-2">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Status History</span>
                </h4>
                
                {history.length > 0 ? (
                  (() => {
                    const groupedHistory = groupHistoryByTime(history)
                    
                    return (
                      <div className="space-y-3">
                        {/* This Week Section */}
                        {groupedHistory.thisWeek.length > 0 && (
                          <div className="border border-gray-700 rounded-lg overflow-hidden">
                            <button
                              onClick={() => toggleHistorySection('thisWeek')}
                              className="w-full px-3 py-2 bg-gray-800/50 hover:bg-gray-800/70 transition-colors flex items-center justify-between text-left"
                            >
                              <div className="flex items-center space-x-2">
                                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="text-base font-medium text-white">This Week</span>
                                <span className="text-sm text-gray-400 bg-blue-100 dark:bg-blue-900/30 px-1.5 py-0.5 rounded-full">
                                  {groupedHistory.thisWeek.length}
                                </span>
                              </div>
                              <svg 
                                className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${expandedHistorySections.thisWeek ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {expandedHistorySections.thisWeek && (
                              <div className="p-3 bg-gray-800/50 border-t border-gray-700 space-y-2">
                                {groupedHistory.thisWeek.map((entry) => {
                                  const entryStatusConfig = getStatusConfig(entry.new_status)
                                  return (
                                    <div key={entry.timestamp} className="flex items-start space-x-2">
                                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium border ${entryStatusConfig.color} flex-shrink-0`}>
                                        <svg className="w-2.5 h-2.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={entryStatusConfig.icon} />
                                        </svg>
                                        {entryStatusConfig.label}
                                      </div>
                                      <div className="text-sm text-gray-400 pt-0.5">
                                        {formatDate(entry.timestamp)}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* This Month Section */}
                        {groupedHistory.thisMonth.length > 0 && (
                          <div className="border border-gray-700 rounded-lg overflow-hidden">
                            <button
                              onClick={() => toggleHistorySection('thisMonth')}
                              className="w-full px-3 py-2 bg-gray-800/50 hover:bg-gray-800/70 transition-colors flex items-center justify-between text-left"
                            >
                              <div className="flex items-center space-x-2">
                                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="text-base font-medium text-white">This Month</span>
                                <span className="text-sm text-gray-400 bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 rounded-full">
                                  {groupedHistory.thisMonth.length}
                                </span>
                              </div>
                              <svg 
                                className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${expandedHistorySections.thisMonth ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {expandedHistorySections.thisMonth && (
                              <div className="p-3 bg-gray-800/50 border-t border-gray-700 space-y-2">
                                {groupedHistory.thisMonth.map((entry) => {
                                  const entryStatusConfig = getStatusConfig(entry.new_status)
                                  return (
                                    <div key={entry.timestamp} className="flex items-start space-x-2">
                                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium border ${entryStatusConfig.color} flex-shrink-0`}>
                                        <svg className="w-2.5 h-2.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={entryStatusConfig.icon} />
                                        </svg>
                                        {entryStatusConfig.label}
                                      </div>
                                      <div className="text-sm text-gray-400 pt-0.5">
                                        {formatDate(entry.timestamp)}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Older Section */}
                        {groupedHistory.older.length > 0 && (
                          <div className="border border-gray-700 rounded-lg overflow-hidden">
                            <button
                              onClick={() => toggleHistorySection('older')}
                              className="w-full px-3 py-2 bg-gray-800/50 hover:bg-gray-800/70 transition-colors flex items-center justify-between text-left"
                            >
                              <div className="flex items-center space-x-2">
                                <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-base font-medium text-white">Older</span>
                                <span className="text-sm text-gray-400 bg-gray-100 dark:bg-gray-700/50 px-1.5 py-0.5 rounded-full">
                                  {groupedHistory.older.length}
                                </span>
                              </div>
                              <svg 
                                className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${expandedHistorySections.older ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {expandedHistorySections.older && (
                              <div className="p-3 bg-gray-800/50 border-t border-gray-700 space-y-2">
                                {groupedHistory.older.map((entry) => {
                                  const entryStatusConfig = getStatusConfig(entry.new_status)
                                  return (
                                    <div key={entry.timestamp} className="flex items-start space-x-2">
                                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium border ${entryStatusConfig.color} flex-shrink-0`}>
                                        <svg className="w-2.5 h-2.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={entryStatusConfig.icon} />
                                        </svg>
                                        {entryStatusConfig.label}
                                      </div>
                                      <div className="text-sm text-gray-400 pt-0.5">
                                        {formatDate(entry.timestamp)}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })()
                ) : (
                  <div className="text-base text-gray-500 italic">
                    No status history available yet.
                  </div>
                )}
              </div>
            </div>
            </>
            )}
            </div>
          </div>


              </div>
              <div className="lg:col-span-3 mt-0 md:mt-12 order-2 lg:order-1">

{/* About Commercial Ideation - Only show when Stage 2 (Commercial Ideation) is active */}
          {currentStage === 2 && (
            <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-visible mt-0 md:mt-16 mb-8">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
              <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-gray-700/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
              <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-gray-700/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
              
              {/* Triangle connector pointing right to Team Progress */}
              <div className="hidden lg:block absolute top-8 -right-[32px] w-0 h-0 border-t-[20px] border-t-transparent border-b-[20px] border-b-transparent border-l-[32px] border-l-white/10 z-20"></div>
              
              {/* Cleo Character - Desktop: Overlapping top border */}
              <div className={`hidden md:block absolute -top-32 left-1/2 -translate-x-1/2 w-64 h-64 z-50 ${getStageStatus(2, currentStage, currentIdea) === 'locked' ? 'grayscale opacity-70' : ''}`}>
                <img
                  src={`${import.meta.env.BASE_URL}characters/Cleo/5_Thumbs_up/5_thumbsup.png`}
                  alt="Cleo"
                  className="w-full h-full object-contain drop-shadow-2xl"
                />
              </div>

              {/* Speech Bubble - Desktop */}
              <div className="hidden md:block absolute -top-16 left-1/2 translate-x-20 z-30">
                <div className="bg-gray-800 border border-gray-700 rounded-xl px-6 py-3 shadow-xl relative max-w-[240px]" style={{ filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.2))' }}>
                  <div className="text-white text-xl" style={{ fontFamily: 'var(--font-handwritten)' }}>
                    Let's map your way into the world
                  </div>
                  {/* Tail pointing to character */}
                  <div className="absolute left-0 top-1/2 -translate-x-[11px] -translate-y-1/2">
                    <div className="w-5 h-5 bg-gray-800 border-l border-b border-gray-700 rotate-45"></div>
                  </div>
                </div>
              </div>

              <div className="relative z-10 pt-0 md:pt-32">
              {/* Mobile: Cleo card matching Orin's style */}
              <div className={`md:hidden bg-gradient-to-r from-cyan-900/30 to-gray-800/50 rounded-xl p-4 border border-cyan-800/30 mb-4 ${getStageStatus(2, currentStage, currentIdea) === 'locked' ? 'grayscale opacity-70' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className="w-20 h-20 rounded-full bg-gray-800 border-2 border-cyan-500 overflow-hidden flex-shrink-0">
                    <img
                      src={`${import.meta.env.BASE_URL}characters/Cleo/5_Thumbs_up/5_thumbsup.png`}
                      alt="Cleo"
                      className="w-full h-full object-cover object-top scale-150 translate-y-2"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs text-cyan-400 font-medium">Cleo says...</span>
                    <p className="text-white text-lg mt-1" style={{ fontFamily: 'var(--font-handwritten)' }}>
                      Let's map your way into the world
                    </p>
                  </div>
                </div>
              </div>
              {/* Feedback banner for locked stages */}
              {getStageStatus(2, currentStage, currentIdea) === 'locked' && (
                <div className="mb-6 bg-purple-900/40 border border-purple-500/50 rounded-xl p-4 flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-800/60 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-purple-200 font-medium mb-1">Coming Soon</h4>
                    <p className="text-purple-300/80 text-sm leading-relaxed">
                      Cleo isn't live yet. Check out her game plan below and let us know what you think—we'd love your input whilst we develop her!
                    </p>
                    <button
                      onClick={() => openFeedbackForTeamMember('Cleo')}
                      className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Give Feedback
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-3">
                <h3 className="text-3xl md:text-3xl font-light tracking-tight text-white" style={{ fontFamily: 'var(--font-handwritten)' }}>
                  My Game Plan
                </h3>
              </div>

              <div className="space-y-4">
                <p className="text-base text-gray-300 leading-relaxed">
                  Once you've completed your work with Orin and your idea scores well, I'll work with you to develop your <span className="font-semibold text-white">complete go-to-market strategy</span>, from finding your perfect customer to building the channels that'll drive growth.
                </p>

                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                  <h3 className="text-lg md:text-xl font-light tracking-tight text-white mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-handwritten)' }}>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Here's what we'll deliver together
                  </h3>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Find Your Perfect Customer:</strong> Who they are, where they hang out, and what makes them tick</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Define Your Launch Strategy:</strong> The channels, tactics, and timeline to make your debut count</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Map Out Your Revenue Model:</strong> How you'll make money and what the numbers could look like</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Establish Your Competitive Position:</strong> Why you'll win and how to stay ahead</span>
                    </li>
                  </ul>
                </div>

              </div>
              </div>
            </div>
          )}

{/* About Business Case - Only show when Stage 3 (Business Case) is active */}
          {currentStage === 3 && (
            <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-visible mt-0 md:mt-16 mb-8">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
              <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-gray-700/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
              <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-gray-700/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>

              {/* Triangle connector pointing right to Team Progress */}
              <div className="hidden lg:block absolute top-8 -right-[32px] w-0 h-0 border-t-[20px] border-t-transparent border-b-[20px] border-b-transparent border-l-[32px] border-l-white/10 z-20"></div>

              {/* Finn Character - Desktop: Overlapping top border */}
              <div className={`hidden md:block absolute -top-32 left-1/2 -translate-x-1/2 w-64 h-64 z-50 ${getStageStatus(3, currentStage, currentIdea) === 'locked' ? 'grayscale opacity-70' : ''}`}>
                <img
                  src={`${import.meta.env.BASE_URL}characters/Finn/1_Default/1_thumbup.png`}
                  alt="Finn"
                  className="w-full h-full object-contain drop-shadow-2xl"
                />
              </div>

              {/* Speech Bubble - Desktop */}
              <div className="hidden md:block absolute -top-16 left-1/2 translate-x-20 z-30">
                <div className="bg-gray-800 border border-gray-700 rounded-xl px-6 py-3 shadow-xl relative max-w-[240px]" style={{ filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.2))' }}>
                  <div className="text-white text-xl" style={{ fontFamily: 'var(--font-handwritten)' }}>
                    Let's package this into a story worth backing
                  </div>
                  {/* Tail pointing to character */}
                  <div className="absolute left-0 top-1/2 -translate-x-[11px] -translate-y-1/2">
                    <div className="w-5 h-5 bg-gray-800 border-l border-b border-gray-700 rotate-45"></div>
                  </div>
                </div>
              </div>

              <div className="relative z-10 pt-0 md:pt-32">
              {/* Mobile: Finn card matching Orin's style */}
              <div className={`md:hidden bg-gradient-to-r from-cyan-900/30 to-gray-800/50 rounded-xl p-4 border border-cyan-800/30 mb-4 ${getStageStatus(3, currentStage, currentIdea) === 'locked' ? 'grayscale opacity-70' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className="w-20 h-20 rounded-full bg-gray-800 border-2 border-cyan-500 overflow-hidden flex-shrink-0">
                    <img
                      src={`${import.meta.env.BASE_URL}characters/Finn/1_Default/1_thumbup.png`}
                      alt="Finn"
                      className="w-full h-full object-cover object-top scale-150 translate-y-2"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs text-cyan-400 font-medium">Finn says...</span>
                    <p className="text-white text-lg mt-1" style={{ fontFamily: 'var(--font-handwritten)' }}>
                      Let's package this into a story worth backing
                    </p>
                  </div>
                </div>
              </div>
              {/* Feedback banner for locked stages */}
              {getStageStatus(3, currentStage, currentIdea) === 'locked' && (
                <div className="mb-6 bg-purple-900/40 border border-purple-500/50 rounded-xl p-4 flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-800/60 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-purple-200 font-medium mb-1">Coming Soon</h4>
                    <p className="text-purple-300/80 text-sm leading-relaxed">
                      Finn isn't live yet. Check out his game plan below and let us know what you think—we'd love your input whilst we develop him!
                    </p>
                    <button
                      onClick={() => openFeedbackForTeamMember('Finn')}
                      className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Give Feedback
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-3">
                <h3 className="text-3xl md:text-3xl font-light tracking-tight text-white" style={{ fontFamily: 'var(--font-handwritten)' }}>
                  My Game Plan
                </h3>
              </div>

              <div className="space-y-4">
                <p className="text-base text-gray-300 leading-relaxed">
                  Once you've completed your work with Cleo and have a solid go-to-market strategy, I'll work with you to package everything into a <span className="font-semibold text-white">compelling business case</span> that stakeholders and investors can rally behind.
                </p>
                
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                  <h3 className="text-lg md:text-xl font-light tracking-tight text-white mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-handwritten)' }}>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Here's what we'll deliver together
                  </h3>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Synthesize the Insights:</strong> Pull together everything Orin and Cleo discovered into one clear narrative</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Build the Financial Story:</strong> Create projections and identify investment requirements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Address the Risks:</strong> Identify what could go wrong and how to mitigate it</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Package It for Sharing:</strong> Create a business case you can present to stakeholders</span>
                    </li>
                  </ul>
                </div>

              </div>
              </div>
            </div>
          )}

{/* About UX Design - Only show when Stage 4 (UX Design) is active */}
          {currentStage === 4 && (
            <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-visible mt-0 md:mt-16 mb-8">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
              <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-gray-700/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
              <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-gray-700/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>

              {/* Triangle connector pointing right to Team Progress */}
              <div className="hidden lg:block absolute top-8 -right-[32px] w-0 h-0 border-t-[20px] border-t-transparent border-b-[20px] border-b-transparent border-l-[32px] border-l-white/10 z-20"></div>

              {/* Una Character - Desktop: Overlapping top border */}
              <div className={`hidden md:block absolute -top-32 left-1/2 -translate-x-1/2 w-64 h-64 z-50 ${getStageStatus(4, currentStage, currentIdea) === 'locked' ? 'grayscale opacity-70' : ''}`}>
                <img
                  src={`${import.meta.env.BASE_URL}characters/Una/1_Default/1_folded.png`}
                  alt="Una"
                  className="w-full h-full object-contain drop-shadow-2xl"
                />
              </div>

              {/* Speech Bubble - Desktop */}
              <div className="hidden md:block absolute -top-16 left-1/2 translate-x-20 z-30">
                <div className="bg-gray-800 border border-gray-700 rounded-xl px-6 py-3 shadow-xl relative max-w-[240px]" style={{ filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.2))' }}>
                  <div className="text-white text-xl" style={{ fontFamily: 'var(--font-handwritten)' }}>
                    Let's design something users will love
                  </div>
                  {/* Tail pointing to character */}
                  <div className="absolute left-0 top-1/2 -translate-x-[11px] -translate-y-1/2">
                    <div className="w-5 h-5 bg-gray-800 border-l border-b border-gray-700 rotate-45"></div>
                  </div>
                </div>
              </div>

              <div className="relative z-10 pt-0 md:pt-32">
              {/* Mobile: Una card matching Orin's style */}
              <div className={`md:hidden bg-gradient-to-r from-cyan-900/30 to-gray-800/50 rounded-xl p-4 border border-cyan-800/30 mb-4 ${getStageStatus(4, currentStage, currentIdea) === 'locked' ? 'grayscale opacity-70' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className="w-20 h-20 rounded-full bg-gray-800 border-2 border-cyan-500 overflow-hidden flex-shrink-0">
                    <img
                      src={`${import.meta.env.BASE_URL}characters/Una/1_Default/1_folded.png`}
                      alt="Una"
                      className="w-full h-full object-cover object-top scale-150 translate-y-2"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs text-cyan-400 font-medium">Una says...</span>
                    <p className="text-white text-lg mt-1" style={{ fontFamily: 'var(--font-handwritten)' }}>
                      Let's design something users will love
                    </p>
                  </div>
                </div>
              </div>
              {/* Feedback banner for locked stages */}
              {getStageStatus(4, currentStage, currentIdea) === 'locked' && (
                <div className="mb-6 bg-purple-900/40 border border-purple-500/50 rounded-xl p-4 flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-800/60 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-purple-200 font-medium mb-1">Coming Soon</h4>
                    <p className="text-purple-300/80 text-sm leading-relaxed">
                      Una isn't live yet. Check out her game plan below and let us know what you think—we'd love your input whilst we develop her!
                    </p>
                    <button
                      onClick={() => openFeedbackForTeamMember('Una')}
                      className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Give Feedback
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-3">
                <h3 className="text-3xl md:text-3xl font-light tracking-tight text-white" style={{ fontFamily: 'var(--font-handwritten)' }}>
                  My Game Plan
                </h3>
              </div>

              <div className="space-y-4">
                <p className="text-base text-gray-300 leading-relaxed">
                  Once Finn has packaged up the business case, I'll work with you to create <span className="font-semibold text-white">user-centered designs and experiences</span> that people will actually love using.
                </p>
                
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                  <h3 className="text-lg md:text-xl font-light tracking-tight text-white mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-handwritten)' }}>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Here's what we'll deliver together
                  </h3>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Get to Know Your Users:</strong> Understand their needs, behaviors, and what makes them tick</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Sketch Out the Experience:</strong> Create wireframes and interactive prototypes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Build Your Visual Language:</strong> Establish the look, feel, and component library</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Validate with Real People:</strong> Test designs with users and iterate until it feels right</span>
                    </li>
                  </ul>
                </div>

              </div>
              </div>
            </div>
          )}

{/* About MVP Development - Only show when Stage 5 (MVP Development) is active */}
          {currentStage === 5 && (
            <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-visible mt-0 md:mt-16 mb-8">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
              <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-gray-700/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
              <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-gray-700/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>

              {/* Triangle connector pointing right to Team Progress */}
              <div className="hidden lg:block absolute top-8 -right-[32px] w-0 h-0 border-t-[20px] border-t-transparent border-b-[20px] border-b-transparent border-l-[32px] border-l-white/10 z-20"></div>

              {/* Vera Character - Desktop: Overlapping top border */}
              <div className={`hidden md:block absolute -top-36 left-1/2 -translate-x-1/2 w-72 h-72 z-50 ${getStageStatus(5, currentStage, currentIdea) === 'locked' ? 'grayscale opacity-70' : ''}`}>
                <img
                  src={`${import.meta.env.BASE_URL}characters/Vera/1_Default/1_folded.png`}
                  alt="Vera"
                  className="w-full h-full object-contain drop-shadow-2xl"
                />
              </div>

              {/* Speech Bubble - Desktop */}
              <div className="hidden md:block absolute -top-16 left-1/2 translate-x-20 z-30">
                <div className="bg-gray-800 border border-gray-700 rounded-xl px-6 py-3 shadow-xl relative max-w-[240px]" style={{ filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.2))' }}>
                  <div className="text-white text-xl" style={{ fontFamily: 'var(--font-handwritten)' }}>
                    Let's make this real
                  </div>
                  {/* Tail pointing to character */}
                  <div className="absolute left-0 top-1/2 -translate-x-[11px] -translate-y-1/2">
                    <div className="w-5 h-5 bg-gray-800 border-l border-b border-gray-700 rotate-45"></div>
                  </div>
                </div>
              </div>

              <div className="relative z-10 pt-0 md:pt-32">
              {/* Mobile: Vera card matching Orin's style */}
              <div className={`md:hidden bg-gradient-to-r from-cyan-900/30 to-gray-800/50 rounded-xl p-4 border border-cyan-800/30 mb-4 ${getStageStatus(5, currentStage, currentIdea) === 'locked' ? 'grayscale opacity-70' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className="w-20 h-20 rounded-full bg-gray-800 border-2 border-cyan-500 overflow-hidden flex-shrink-0">
                    <img
                      src={`${import.meta.env.BASE_URL}characters/Vera/1_Default/1_folded.png`}
                      alt="Vera"
                      className="w-full h-full object-cover object-top scale-150 translate-y-2"
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-xs text-cyan-400 font-medium">Vera says...</span>
                    <p className="text-white text-lg mt-1" style={{ fontFamily: 'var(--font-handwritten)' }}>
                      Let's make this real
                    </p>
                  </div>
                </div>
              </div>
              {/* Feedback banner for locked stages */}
              {getStageStatus(5, currentStage, currentIdea) === 'locked' && (
                <div className="mb-6 bg-purple-900/40 border border-purple-500/50 rounded-xl p-4 flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-800/60 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-purple-200 font-medium mb-1">Coming Soon</h4>
                    <p className="text-purple-300/80 text-sm leading-relaxed">
                      Vera isn't live yet. Check out her game plan below and let us know what you think—we'd love your input whilst we develop her!
                    </p>
                    <button
                      onClick={() => openFeedbackForTeamMember('Vera')}
                      className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Give Feedback
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-3">
                <h3 className="text-3xl md:text-3xl font-light tracking-tight text-white" style={{ fontFamily: 'var(--font-handwritten)' }}>
                  My Game Plan
                </h3>
              </div>

              <div className="space-y-4">
                <p className="text-base text-gray-300 leading-relaxed">
                  Once Una has nailed the design, I'll work with you to <span className="font-semibold text-white">build, test, and ship your MVP</span>. No overthinking, just action — let's get this thing out into the world.
                </p>

                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                  <h3 className="text-lg md:text-xl font-light tracking-tight text-white mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-handwritten)' }}>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Here's what we'll deliver together
                  </h3>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Architect the Foundation:</strong> Design a scalable, maintainable system that can grow with you</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Build the Core Features:</strong> Develop the essential functionality for your MVP launch</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Test Everything Thoroughly:</strong> Make sure it works right before it goes live</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-gray-400 mt-1 flex-shrink-0">•</span>
                      <span><strong className="text-white">Ship It to the World:</strong> Deploy and get it in front of real users</span>
                    </li>
                  </ul>
                </div>

              </div>
              </div>
            </div>
          )}

{/* Opportunity Overview - Always show on Stage 1 */}
          {currentStage === 1 && (
            <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-visible mt-0 md:mt-16">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
              <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-cyan-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
              <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-cyan-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
              
              {/* Triangle connector pointing right to Assessment Progress */}
              <div className="hidden lg:block absolute top-8 -right-[32px] w-0 h-0 border-t-[20px] border-t-transparent border-b-[20px] border-b-transparent border-l-[32px] border-l-white/10 z-20"></div>
              
              {/* Orin Character - Always show with appropriate image */}
              {(() => {
                const hasScore = currentIdea?.opportunity_assessment?.score
                const needsRefinement = currentIdea?.status === 'idea-refinement'
                const isInProgress = !hasScore && !needsRefinement && (
                  currentIdea?.status === 'triage' ||
                  currentIdea?.status === 'opportunity-assessment' ||
                  currentIdea?.status === 'search-terms-generated' ||
                  currentIdea?.status === 'web-searches-completed' ||
                  currentIdea?.status === 'market-demand-evaluated' ||
                  currentIdea?.status === 'market-saturation-evaluated' ||
                  currentIdea?.status === 'usp-evaluated'
                )
                
                // Determine which image to show based on state priority:
                // 1. Has score -> use score-based pose
                // 2. Needs refinement -> idea pose
                // 3. No score yet (in progress or any other state) -> thinking pose
                const basePath = import.meta.env.BASE_URL
                let orinImage
                if (hasScore) {
                  orinImage = getOrinPoseForScore(currentIdea.opportunity_assessment.score)
                } else if (needsRefinement) {
                  orinImage = `${basePath}characters/Orin/3_Has_Idea/3_idea.png`
                } else {
                  // Default to thinking for any state without a score
                  orinImage = `${basePath}characters/Orin/2_Thinking/2_thinking.png`
                }
                
                return (
                  <>
                    {/* Orin Character - Overlapping top border (hidden on mobile) */}
                    <div className={`hidden md:block absolute ${hasScore ? getOrinTopPosition(currentIdea.opportunity_assessment.score) : needsRefinement ? '-top-32' : '-top-[9.2rem]'} left-1/2 -translate-x-1/2 ${hasScore ? getOrinSizeClass(currentIdea.opportunity_assessment.score) : needsRefinement ? 'w-64 h-64' : 'w-64 h-64'} z-50`}>
                      <img 
                        src={orinImage}
                        alt="Orin"
                        className="w-full h-full object-contain drop-shadow-2xl"
                      />
                    </div>
                    
                    {/* Score Speech Bubble - Only show when score exists (hidden on mobile) */}
                    {hasScore && (
                      <div className="hidden md:block absolute -top-20 left-1/2 translate-x-28 z-30">
                        <div className="bg-gray-800 border border-gray-700 rounded-xl px-6 py-3 shadow-xl relative max-w-[220px]" style={{ filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.2))' }}>
                          <div className="text-white text-2xl" style={{ fontFamily: 'var(--font-handwritten)' }}>
                            {getOrinScoreMessage(currentIdea.opportunity_assessment.score).split(currentIdea.opportunity_assessment.score.toString()).map((part, i, arr) => (
                              i === arr.length - 1 ? part : (
                                <span key={i}>
                                  {part}
                                  <span className={`text-4xl font-bold ${currentIdea.opportunity_assessment.score > 6.9 ? 'text-green-400' : currentIdea.opportunity_assessment.score >= 5 ? 'text-yellow-400' : 'text-red-400'}`}>
                                    {currentIdea.opportunity_assessment.score}
                                  </span>
                                  <span className="text-gray-400 text-3xl">/10</span>
                                </span>
                              )
                            ))}
                          </div>
                          {/* Tail pointing to Orin */}
                          <div className="absolute left-0 top-1/2 -translate-x-[11px] -translate-y-1/2">
                            <div className="w-5 h-5 bg-gray-800 border-l border-b border-gray-700 rotate-45"></div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Thought Bubble - Show when thinking/in progress (hidden on mobile) */}
                    {isInProgress && (
                      <div className="hidden md:block absolute -top-28 left-1/2 translate-x-20 z-30">
                        <div className="bg-gray-800 border border-gray-700 rounded-full px-8 py-5 shadow-xl relative max-w-[240px]" style={{ filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.2))' }}>
                          <div className="text-white text-xl italic" style={{ fontFamily: 'var(--font-handwritten)' }}>
                            Working through your idea...
                          </div>
                          {/* Thought bubble circles pointing to Orin's head */}
                          <div className="absolute left-8 bottom-4 translate-y-6 -translate-x-2">
                            <div className="w-3 h-3 bg-gray-800 border border-gray-700 rounded-full" style={{ filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.2))' }}></div>
                          </div>
                          <div className="absolute left-6 bottom-2 translate-y-10 -translate-x-4">
                            <div className="w-2 h-2 bg-gray-800 border border-gray-700 rounded-full" style={{ filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.2))' }}></div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Speech Bubble - Show when idea needs refinement (hidden on mobile) */}
                    {needsRefinement && (
                      <div className="hidden md:block absolute -top-16 left-1/2 translate-x-20 z-30">
                        <div className="bg-gray-800 border border-gray-700 rounded-xl px-6 py-3 shadow-xl relative max-w-[240px]" style={{ filter: 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.2))' }}>
                          <div className="text-white text-xl" style={{ fontFamily: 'var(--font-handwritten)' }}>
                            I need your help to move this forward...
                          </div>
                          {/* Tail pointing to Orin */}
                          <div className="absolute left-0 top-1/2 -translate-x-[11px] -translate-y-1/2">
                            <div className="w-5 h-5 bg-gray-800 border-l border-b border-gray-700 rotate-45"></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )
              })()}
              
              <div className={`relative z-10 ${currentIdea?.opportunity_assessment?.score ? 'pt-0 md:pt-20' : 'pt-0 md:pt-20'}`}>

              {/* In Progress Message - Show when no score yet */}
              {!currentIdea?.opportunity_assessment?.score && (
                <>
                  {/* Mobile: Card style display for in-progress/refinement states */}
                  <div className="md:hidden bg-gradient-to-r from-cyan-900/30 to-gray-800/50 rounded-xl p-4 border border-cyan-800/30 mb-4">
                    <div className="flex items-start gap-3">
                      {/* Mini Orin avatar */}
                      <div className="w-14 h-14 rounded-full bg-gray-800 border-2 border-cyan-500 overflow-hidden flex-shrink-0">
                        <img
                          src={currentIdea?.status === 'idea-refinement'
                            ? `${import.meta.env.BASE_URL}characters/Orin/3_Has_Idea/3_idea.png`
                            : `${import.meta.env.BASE_URL}characters/Orin/2_Thinking/2_thinking.png`}
                          alt="Orin"
                          className="w-full h-full object-cover object-top scale-150 translate-y-2"
                        />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs text-cyan-400 font-medium">
                          {currentIdea?.status === 'idea-refinement' ? "Orin's Feedback" : "Orin's Working..."}
                        </span>
                        <p className="text-sm text-gray-300 mt-1">
                          {currentIdea?.status === 'idea-refinement'
                            ? "I've got some thoughts to share. Check the feedback section below."
                            : "I'm analyzing your idea..."}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Desktop: Original centered message */}
                  <div className="hidden md:block text-center pb-4 pt-10">
                    <p className="text-gray-300 text-2xl italic" style={{ fontFamily: 'var(--font-handwritten)' }}>
                      {currentIdea?.status === 'idea-refinement'
                        ? "I've reviewed your idea and have some thoughts. Check my feedback on the right to start refining it together."
                        : ASSISTANTS.orin.in_progress}
                    </p>
                  </div>
                </>
              )}

              {/* Evaluation Breakdown - Only show when opportunity score exists */}
              {currentIdea?.opportunity_assessment?.score && (
              <div className="mb-6">
                {/* Mobile: CardStyleWithMiniOrin score display */}
                <div className="md:hidden bg-gradient-to-r from-cyan-900/30 to-gray-800/50 rounded-xl p-4 border border-cyan-800/30 mb-4">
                  <div className="flex items-start gap-3">
                    {/* Mini Orin avatar */}
                    <div className="w-20 h-20 rounded-full bg-gray-800 border-2 border-cyan-500 overflow-hidden flex-shrink-0">
                      <img
                        src={getOrinPoseForScore(currentIdea.opportunity_assessment.score)}
                        alt="Orin"
                        className="w-full h-full object-cover object-top scale-150 translate-y-2"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-cyan-400 font-medium">Orin's Assessment</span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className={`text-4xl font-bold ${currentIdea.opportunity_assessment.score > 6.9 ? 'text-green-400' : currentIdea.opportunity_assessment.score >= 5 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {currentIdea.opportunity_assessment.score}
                        </span>
                        <span className="text-gray-400 text-lg">/10</span>
                      </div>
                      <p className="text-xs text-gray-300 mt-1">
                        {currentIdea.opportunity_assessment.score >= 7 ? 'Strong opportunity potential' :
                         currentIdea.opportunity_assessment.score >= 5 ? 'Moderate opportunity' : 'Needs work'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Let's Break It Down */}
                <div id="orin-assessment">
                  <h4 className="font-display text-2xl md:text-2xl font-light tracking-tight text-white mb-3">Let&apos;s Break It Down</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="rounded-lg p-4 text-center border bg-gray-800 border-gray-700">
                    <div className="text-sm text-gray-300 mb-1">Demand</div>
                    <div className={`text-xl font-bold ${
                      getScoreTextClass(currentIdea.market_demand_evaluation?.score)
                    }`}>
                      {currentIdea.market_demand_evaluation?.score || 'N/A'}/10
                    </div>
                  </div>
                  <div className="rounded-lg p-4 text-center border bg-gray-800 border-gray-700">
                    <div className="text-sm text-gray-300 mb-1">Competition</div>
                    <div className={`text-xl font-bold ${
                      getSaturationTextClass(currentIdea.market_saturation_evaluation?.score)
                    }`}>
                      {currentIdea.market_saturation_evaluation?.score || 'N/A'}/10
                    </div>
                  </div>
                  <div className="rounded-lg p-4 text-center border bg-gray-800 border-gray-700">
                    <div className="text-sm text-gray-300 mb-1">Your Advantage</div>
                    <div className={`text-xl font-bold ${
                      getScoreTextClass(currentIdea.usp_evaluation?.score)
                    }`}>
                      {currentIdea.usp_evaluation?.score || 'N/A'}/10
                    </div>
                  </div>
                </div>

                  <h4 className="font-display text-2xl md:text-2xl font-light tracking-tight text-white mb-3 mt-6">My Take</h4>
                  {(() => {
                  const reasoning = currentIdea?.opportunity_assessment?.reason
                  if (!reasoning) return (
                    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                      <div className="text-gray-400 text-base italic">
                        I'm still working through your idea... Check back soon for my full assessment!
                      </div>
                    </div>
                  )
                  
                  // Parse structured feedback
                  const summaryMatch = reasoning.match(/\*\*SUMMARY:\*\*\s*([^*]+?)(?=\n\n|\*\*|$)/s)
                  const summary = summaryMatch ? summaryMatch[1].trim() : null
                  
                  // Extract sections - different names per step type
                  const whyWorkMatch = reasoning.match(/\*\*(?:🚀 )?Why This Could Work:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
                  const holdBackMatch = reasoning.match(/\*\*(?:⚠️ )?What Could Hold It Back:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
                  const nextStepsMatch = reasoning.match(/\*\*(?:🎯 )?Next Steps to Consider:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
                  
                  // Legacy format fallback
                  const prosMatch = reasoning.match(/\*\*(?:✅ )?Pros:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
                  const consMatch = reasoning.match(/\*\*(?:⚠️ )?Cons:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
                  const recommendationsMatch = reasoning.match(/\*\*(?:💡 )?Recommendations:\*\*\s*([^*]+?)(?=\n\n\*\*|$)/s)
                  
                  // Helper to parse section content
                  const parseSection = (content) => {
                    if (!content) return null
                    const lines = content.trim().split('\n').filter(line => line.trim())
                    const bullets = lines.filter(line => line.trim().startsWith('-'))
                    const summaryLines = lines.filter(line => !line.trim().startsWith('-'))
                    return {
                      summary: summaryLines.join(' ').trim(),
                      bullets: bullets.map(b => b.trim().replace(/^-\s*/, ''))
                    }
                  }
                  
                  // Determine which sections to use
                  let section1, section2, section3
                  let section1Title, section2Title, section3Title
                  let section1Emoji, section2Emoji, section3Emoji
                  
                  if (whyWorkMatch && holdBackMatch) {
                    // New Opportunity Assessment format
                    section1 = parseSection(whyWorkMatch[1])
                    section2 = parseSection(holdBackMatch[1])
                    section3 = nextStepsMatch ? parseSection(nextStepsMatch[1]) : null
                    section1Title = "Why This Could Work"
                    section2Title = "What Could Hold It Back"
                    section3Title = "Next Steps to Consider"
                    section1Emoji = "🚀"
                    section2Emoji = "⚠️"
                    section3Emoji = "🎯"
                  } else {
                    // Legacy Pros/Cons/Recommendations format
                    section1 = prosMatch ? parseSection(prosMatch[1]) : null
                    section2 = consMatch ? parseSection(consMatch[1]) : null
                    section3 = recommendationsMatch ? parseSection(recommendationsMatch[1]) : null
                    section1Title = "Pros"
                    section2Title = "Cons"
                    section3Title = "Recommendations"
                    section1Emoji = "✅"
                    section2Emoji = "⚠️"
                    section3Emoji = "💡"
                  }
                  
                  const hasStructuredFeedback = summary || section1 || section2
                  
                  if (hasStructuredFeedback) {
                    return (
                      <div>
                        {/* Summary */}
                        {summary && (
                          <p className="text-base text-gray-300 leading-relaxed mb-4">{summary}</p>
                        )}
                        
                        {/* Sections - each in its own box, collapsed by default */}
                        <div className="space-y-3">
                          {section1 && (
                            <div className="rounded-lg p-4 border bg-gray-800/50 border-gray-700">
                              <h6 className="text-base font-semibold text-cyan-400 mb-2 flex items-center gap-2">
                                <span>{section1Emoji}</span> {section1Title}
                              </h6>
                              {section1.summary && (
                                <p className="text-gray-300 text-base mb-2">{section1.summary}</p>
                              )}
                              {section1.bullets.length > 0 && (
                                <details className="group">
                                  <summary className="text-base text-gray-400 cursor-pointer hover:text-gray-300 transition-colors list-none flex items-center gap-1 mb-2">
                                    <span className="inline-block transition-transform group-open:rotate-90">▸</span>
                                    Show details ({section1.bullets.length})
                                  </summary>
                                  <div className="pl-3 border-l border-gray-700">
                                    <div className="text-gray-300 text-base space-y-1.5">
                                      {section1.bullets.map((bullet, idx) => (
                                        <div key={`s1-${idx}`} className="flex items-start gap-2">
                                          <span className="text-green-400/50 mt-0.5 flex-shrink-0 text-xs">•</span>
                                          <span>{bullet}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </details>
                              )}
                            </div>
                          )}
                          
                          {section2 && (
                            <div className="rounded-lg p-4 border bg-gray-800/50 border-gray-700">
                              <h6 className="text-base font-semibold text-cyan-400 mb-2 flex items-center gap-2">
                                <span>{section2Emoji}</span> {section2Title}
                              </h6>
                              {section2.summary && (
                                <p className="text-gray-300 text-base mb-2">{section2.summary}</p>
                              )}
                              {section2.bullets.length > 0 && (
                                <details className="group">
                                  <summary className="text-base text-gray-400 cursor-pointer hover:text-gray-300 transition-colors list-none flex items-center gap-1 mb-2">
                                    <span className="inline-block transition-transform group-open:rotate-90">▸</span>
                                    Show details ({section2.bullets.length})
                                  </summary>
                                  <div className="pl-3 border-l border-gray-700">
                                    <div className="text-gray-300 text-base space-y-1.5">
                                      {section2.bullets.map((bullet, idx) => (
                                        <div key={`s2-${idx}`} className="flex items-start gap-2">
                                          <span className="text-yellow-400/50 mt-0.5 flex-shrink-0 text-xs">•</span>
                                          <span>{bullet}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </details>
                              )}
                            </div>
                          )}
                          
                          {section3 && (
                            <div className="rounded-lg p-4 border bg-gray-800/50 border-gray-700">
                              <h6 className="text-base font-semibold text-cyan-400 mb-2 flex items-center gap-2">
                                <span>{section3Emoji}</span> {section3Title}
                              </h6>
                              {section3.summary && (
                                <p className="text-gray-300 text-base mb-2">{section3.summary}</p>
                              )}
                              {section3.bullets.length > 0 && (
                                <details className="group">
                                  <summary className="text-base text-gray-400 cursor-pointer hover:text-gray-300 transition-colors list-none flex items-center gap-1 mb-2">
                                    <span className="inline-block transition-transform group-open:rotate-90">▸</span>
                                    Show details ({section3.bullets.length})
                                  </summary>
                                  <div className="pl-3 border-l border-gray-700">
                                    <div className="text-gray-300 text-base space-y-1.5">
                                      {section3.bullets.map((bullet, idx) => (
                                        <div key={`s3-${idx}`} className="flex items-start gap-2">
                                          <span className="text-cyan-400/50 mt-0.5 flex-shrink-0 text-xs">•</span>
                                          <span>{bullet}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </details>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  }
                  
                  // Fallback for old unstructured format
                  return (
                    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                      <div className="text-gray-300 text-base">
                        {formatAssessmentReason(reasoning, currentIdea?.opportunity_assessment?.score, false)}
                      </div>
                    </div>
                  )
                })()}

                {/* Next Steps Panel */}
                <div className="mt-6">
                  <h4 className="font-display text-2xl md:text-2xl font-light tracking-tight text-white mb-4">Next Steps</h4>
                  <NextStepsPanel
                    taskCount={orinTaskCount}
                    isGenerating={generatingInsights}
                    onViewTasks={() => { setActiveTab('tasks'); window.scrollTo(0, 0); }}
                    onViewWhiteboard={() => { setActiveTab('whiteboard'); window.scrollTo(0, 0); }}
                    onAskOrin={() => setIsAskOrinOpen(true)}
                    onDownloadReport={() => { setActiveTab('reports'); window.scrollTo(0, 0); }}
                    onProgressToCleo={() => { goToStage(2); window.scrollTo(0, 0); }}
                    variant="minimal"
                  />
                </div>
                </div>
              </div>
              )}

              </div>
            </div>
          )}

                {/* Right column content */}


   <div className={`lg:col-span-3 space-y-8 order-2 lg:order-1 ${currentStage === 1 ? 'mt-8' : ''}`}>

          {/* Mobile-only My Progress - Stage 1 uses IdeaProgress, others show Coming soon */}
          {currentStage === 1 && !isEditing && (
            <div className="md:hidden mb-6">
              <IdeaProgress
                currentIdea={currentIdea}
                formatDate={formatDate}
                isExpanded={isAssessmentProgressExpanded}
                onToggle={() => setIsAssessmentProgressExpanded(!isAssessmentProgressExpanded)}
                onRefineIdea={() => setIsRefineModalOpen(true)}
                onArchiveIdea={toggleArchiveIdea}
                archiving={archiving}
                onProgressToMarketResearch={async () => {
                  try {
                    setSkipping(true)
                    await apiService.forceProgressIdea(currentIdea.id)
                    const refreshedIdea = await apiService.getIdea(currentIdea.id)
                    setCurrentIdea(refreshedIdea)
                    const historyData = await apiService.getIdeaHistory(currentIdea.id)
                    setHistory(historyData.status_history || [])
                  } catch (error) {
                    console.error('Failed to progress idea:', error)
                  } finally {
                    setSkipping(false)
                  }
                }}
                skipping={skipping}
                history={history}
              />
            </div>
          )}
          {currentStage >= 2 && currentStage <= 5 && !isEditing && (
            <div className="md:hidden rounded-2xl bg-gradient-to-br from-gray-700/60 to-gray-700/60 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 relative overflow-hidden mb-6">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
              <div className="relative z-10">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center shadow-lg">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="font-display text-xl font-light tracking-tight text-white">My Progress</h3>
                    <p className="text-xs text-gray-400">Step 0 of 4</p>
                  </div>
                </div>
                <div className="w-full bg-gray-600 rounded-full h-2 mb-3">
                  <div className="bg-gradient-to-r from-gray-500 to-gray-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                </div>
                <p className="text-sm text-gray-400 italic" style={{ fontFamily: 'var(--font-handwritten)' }}>Coming soon...</p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
            <div className={`absolute top-0 left-0 w-96 h-40 bg-gradient-to-br ${currentStage === 1 ? 'from-cyan-900/20' : 'from-gray-700/20'} via-transparent to-transparent rounded-tl-2xl pointer-events-none`}></div>
            <div className={`absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl ${currentStage === 1 ? 'from-cyan-900/20' : 'from-gray-700/20'} via-transparent to-transparent rounded-br-2xl pointer-events-none`}></div>
            <div className="relative z-10">
            <button
              onClick={() => setIsIdeaDetailsExpanded(!isIdeaDetailsExpanded)}
              className={`w-full lg:pointer-events-none flex items-start justify-between gap-3 text-white lg:hover:opacity-100 hover:opacity-80 transition-opacity ${isIdeaDetailsExpanded ? 'mb-6' : 'mb-0'}`}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 bg-gradient-to-br ${currentStage === 1 ? 'from-cyan-500 to-cyan-600' : 'from-gray-500 to-gray-600'} rounded-xl flex items-center justify-center shadow-lg`}>
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className="text-left">
                  <h1 className="font-display text-2xl md:text-3xl font-light tracking-tight text-white">
                    My Idea
                  </h1>
                  <p className="text-base text-gray-300 mt-1">
                    {currentIdea.title || currentIdea.what?.substring(0, 60) + (currentIdea.what?.length > 60 ? '...' : '')}
                  </p>
                </div>
              </div>
              <svg className={`lg:hidden w-6 h-6 transition-transform ${isIdeaDetailsExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isIdeaDetailsExpanded && (
              <div className="hidden lg:flex items-center space-x-3 absolute top-6 right-6">
                {!isEditing && (
                  <button
                    onClick={handleEdit}
                    className={`px-4 py-2 text-white rounded-xl transition-colors duration-200 text-sm font-medium shadow-lg hover:shadow-xl ${currentStage === 1 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-500'}`}
                  >
                    Edit Idea
                  </button>
                )}
              </div>
            )}
            {isIdeaDetailsExpanded && !isEditing && (
              <div className="lg:hidden mb-6 ml-16">
                <button
                  onClick={handleEdit}
                  className={`w-full px-4 py-2 text-white rounded-xl transition-colors duration-200 text-sm font-medium shadow-lg hover:shadow-xl ${currentStage === 1 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 hover:bg-gray-500'}`}
                >
                  Edit Idea
                </button>
              </div>
            )}

            {isIdeaDetailsExpanded && (
            <>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700 font-medium">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Content Sections */}
            <div className="space-y-8">
              {/* What Section */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <div className={`w-8 h-8 bg-gradient-to-r ${currentStage === 1 ? 'from-cyan-500 to-cyan-600' : 'from-gray-500 to-gray-600'} rounded-lg flex items-center justify-center`}>
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <h3 className="font-display text-lg font-light tracking-tight text-white">What is your idea?</h3>
                </div>
                {isEditing ? (
                  <div className="ml-10">
                    <textarea
                      value={editForm.what}
                      onChange={(e) => setEditForm(prev => ({ ...prev, what: e.target.value }))}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-base text-white placeholder-gray-500 resize-none" />
                  </div>
                ) : (
                  <div className="ml-10 bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                    <p className="text-gray-300 text-base leading-relaxed whitespace-pre-wrap">
                      {currentIdea.what}
                    </p>
                  </div>
                )}
              </div>

              {/* Who Section */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <div className={`w-8 h-8 bg-gradient-to-r ${currentStage === 1 ? 'from-cyan-500 to-cyan-600' : 'from-gray-500 to-gray-600'} rounded-lg flex items-center justify-center`}>
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <h3 className="font-display text-lg font-light tracking-tight text-white">Who is your target audience?</h3>
                </div>
                {isEditing ? (
                  <div className="ml-10">
                    <textarea
                      value={editForm.who}
                      onChange={(e) => setEditForm(prev => ({ ...prev, who: e.target.value }))}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-base text-white placeholder-gray-500 resize-none" />
                  </div>
                ) : (
                  <div className="ml-10 bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                    <p className="text-gray-300 text-base leading-relaxed whitespace-pre-wrap">
                      {currentIdea.who}
                    </p>
                  </div>
                )}
              </div>

              {/* Why Section */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <div className={`w-8 h-8 bg-gradient-to-r ${currentStage === 1 ? 'from-cyan-500 to-cyan-600' : 'from-gray-500 to-gray-600'} rounded-lg flex items-center justify-center`}>
                    <span className="text-white text-sm font-bold">3</span>
                  </div>
                  <h3 className="font-display text-lg font-light tracking-tight text-white">Why is this valuable?</h3>
                </div>
                {isEditing ? (
                  <div className="ml-10">
                    <textarea
                      value={editForm.why}
                      onChange={(e) => setEditForm(prev => ({ ...prev, why: e.target.value }))}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-base text-white placeholder-gray-500 resize-none" />
                  </div>
                ) : (
                  <div className="ml-10 bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                    <p className="text-gray-300 text-base leading-relaxed whitespace-pre-wrap">
                      {currentIdea.why}
                    </p>
                  </div>
                )}
              </div>

              {/* What Expected Section */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <div className={`w-8 h-8 bg-gradient-to-r ${currentStage === 1 ? 'from-cyan-500 to-cyan-600' : 'from-gray-500 to-gray-600'} rounded-lg flex items-center justify-center`}>
                    <span className="text-white text-sm font-bold">4</span>
                  </div>
                  <h3 className="font-display text-lg font-light tracking-tight text-white">What do you expect them to achieve using this?</h3>
                </div>
                {isEditing ? (
                  <div className="ml-10">
                    <textarea
                      value={editForm.what_expected}
                      onChange={(e) => setEditForm(prev => ({ ...prev, what_expected: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-base text-white placeholder-gray-500 resize-none"
                      placeholder="Describe the specific outcomes, goals, or benefits your users will experience..." />
                  </div>
                ) : (
                  <div className="ml-10 bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                    <p className="text-gray-300 text-base leading-relaxed whitespace-pre-wrap">
                      {currentIdea.what_expected || 'No expected outcomes specified'}
                    </p>
                  </div>
                )}
              </div>

              {/* Make Money Section */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <div className={`w-8 h-8 bg-gradient-to-r ${currentStage === 1 ? 'from-cyan-500 to-cyan-600' : 'from-gray-500 to-gray-600'} rounded-lg flex items-center justify-center`}>
                    <span className="text-white text-sm font-bold">5</span>
                  </div>
                  <h3 className="font-display text-lg font-light tracking-tight text-white">Do you want to make money from this idea?</h3>
                </div>
                {isEditing ? (
                  <div className="ml-10">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.make_money}
                        onChange={(e) => setEditForm(prev => ({ ...prev, make_money: e.target.checked }))}
                        className="w-5 h-5 text-green-600 bg-gray-900 border-gray-600 rounded focus:ring-green-500 focus:ring-2 transition-all duration-200" />
                      <span className="text-sm text-gray-300 select-none">
                        Yes, I want to commercialize this idea
                      </span>
                    </label>
                  </div>
                ) : (
                  <div className="ml-10 bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                    <div className="flex items-center space-x-2">
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${currentIdea.make_money ? 'bg-green-500 border-green-500' : 'border-gray-600'}`}>
                        {currentIdea.make_money && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="text-gray-300">
                        {currentIdea.make_money ? 'Yes, commercial intent' : 'No commercial intent'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Positive Examples Section */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <div className={`w-8 h-8 bg-gradient-to-r ${currentStage === 1 ? 'from-cyan-500 to-cyan-600' : 'from-gray-500 to-gray-600'} rounded-lg flex items-center justify-center`}>
                    <span className="text-white text-sm font-bold">6</span>
                  </div>
                  <h3 className="font-display text-lg font-light tracking-tight text-white">What positive examples can you share?</h3>
                </div>
                {isEditing ? (
                  <div className="ml-10">
                    <textarea
                      value={editForm.positive_examples}
                      onChange={(e) => setEditForm(prev => ({ ...prev, positive_examples: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-base text-white placeholder-gray-500 resize-none"
                      placeholder="Share existing products you like and how they should inform your creation..." />
                  </div>
                ) : (
                  <div className="ml-10 bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                    <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                      {currentIdea.positive_examples || 'No positive examples provided'}
                    </p>
                  </div>
                )}
              </div>

              {/* Negative Examples Section */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <div className={`w-8 h-8 bg-gradient-to-r ${currentStage === 1 ? 'from-cyan-500 to-cyan-600' : 'from-gray-500 to-gray-600'} rounded-lg flex items-center justify-center`}>
                    <span className="text-white text-sm font-bold">7</span>
                  </div>
                  <h3 className="font-display text-lg font-light tracking-tight text-white">What negative examples can you share?</h3>
                </div>
                {isEditing ? (
                  <div className="ml-10">
                    <textarea
                      value={editForm.negative_examples}
                      onChange={(e) => setEditForm(prev => ({ ...prev, negative_examples: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 bg-gray-900/50 backdrop-blur-sm transition-all duration-200 text-base text-white placeholder-gray-500 resize-none"
                      placeholder="Share similar products that fail or miss the mark and how you'd avoid their mistakes..." />
                  </div>
                ) : (
                  <div className="ml-10 bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                    <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                      {currentIdea.negative_examples || 'No negative examples provided'}
                    </p>
                  </div>
                )}
              </div>

              {/* Edit Actions */}
              {isEditing && (
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200/50 dark:border-gray-700/50">
                  <button
                    onClick={handleCancel}
                    className="px-6 py-3 border border-gray-600 rounded-xl text-gray-300 hover:bg-gray-800 font-medium transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
            </>
            )}
            </div>
          </div>

          {/* Mobile-only Idea Status - Shown after My Idea on mobile */}
          <div className="md:hidden rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
            <div className={`absolute top-0 left-0 w-96 h-40 bg-gradient-to-br ${currentStage === 1 ? 'from-cyan-900/20' : 'from-gray-700/20'} via-transparent to-transparent rounded-tl-2xl pointer-events-none`}></div>
            <div className="relative z-10">
              <button
                onClick={() => setIsIdeaStatusExpanded(!isIdeaStatusExpanded)}
                className={`w-full flex items-center justify-between gap-3 text-white hover:opacity-80 transition-opacity ${isIdeaStatusExpanded ? 'mb-6' : 'mb-0'}`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 bg-gradient-to-br ${currentStage === 1 ? 'from-cyan-500 to-cyan-600' : 'from-gray-500 to-gray-600'} rounded-xl flex items-center justify-center shadow-lg`}>
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="font-display text-2xl font-light tracking-tight text-white">Idea Status</h3>
                    <p className="text-gray-300 text-sm">Current status and progress</p>
                  </div>
                </div>
                <svg className={`w-6 h-6 transition-transform ${isIdeaStatusExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isIdeaStatusExpanded && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-base text-gray-400">Created</span>
                    <span className="text-base font-medium text-gray-100">{formatDate(currentIdea.created_at)}</span>
                  </div>
                  {currentIdea.updated_at && (
                    <div className="flex justify-between items-center">
                      <span className="text-base text-gray-400">Last Updated</span>
                      <span className="text-base font-medium text-gray-100">{formatDate(currentIdea.updated_at)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-base text-gray-400">Current Status</span>
                    <span className="text-base font-medium text-white capitalize">
                      {currentIdea.status?.replace(/-/g, ' ') || 'Unknown'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Main Content */}

     

        {/* Sidebar */}
        <div className="lg:col-span-2 space-y-8">



        </div>
      </div>
      )}

      {/* Whiteboard Tab */}
      <div className={`space-y-8 min-h-[900px] ${activeTab === 'whiteboard' ? 'block' : 'hidden'}`}>
          {/* Whiteboard Explanation Banner */}
          <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-6 py-8 sm:px-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
            <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-blue-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
            <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-indigo-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
            <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <div>
                <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight text-white">
                  Ideation Board
                </h3>
                <p className="text-gray-300 text-sm">
                  Map out your thoughts, reflect on AI feedback, and explore different aspects of your idea.
                </p>
              </div>
            </div>
            <div className="mt-6 flex items-center space-x-4 text-sm text-gray-400">
                  <div className="flex items-center space-x-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <span>Auto-saves your work</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    <span>Syncs across devices</span>
                  </div>
                </div>
            </div>
          </div>
          
          {/* Whiteboard Component - only mount tldraw when tab is active to avoid race conditions */}
          <div className="h-[800px] w-full">
            {activeTab !== 'whiteboard' ? (
              // Placeholder when tab is not active - tldraw will mount fresh when user switches to tab
              <div className="h-full w-full bg-gray-800/30 rounded-xl border border-gray-700/50" />
            ) : !isWhiteboardLoaded ? (
              <div className="h-full w-full flex items-center justify-center bg-gray-800/50 rounded-xl border border-gray-700">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-10 h-10 border-3 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-gray-400 text-sm">Loading whiteboard...</span>
                </div>
              </div>
            ) : (
              <IdeaWhiteboard
                key={`whiteboard-${whiteboardVersion}`}
                ideaId={idea.id}
                whiteboardData={whiteboardData}
                onSave={handleWhiteboardSave}
                onReset={handleWhiteboardReset}
                className="h-full w-full"
              />
            )}
          </div>
        </div>
        
        {/* Tasks Tab */}
        <div
          className={'space-y-8 min-h-[900px] ' + (activeTab === 'tasks' ? 'block' : 'hidden')}
        >
          <TaskManager ref={tasksRef} ideaId={idea.id} onTasksChange={setOpenTaskCount} onOrinTaskCountChange={setOrinTaskCount} highlightTaskId={highlightTaskId} />
        </div>

        {/* Reports Tab */}
        <div className={'space-y-8 min-h-[900px] ' + (activeTab === 'reports' ? 'block' : 'hidden')}>
          <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-6 py-8 sm:px-8 relative overflow-hidden">
            {/* Top border glow */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
            
            {/* Background gradient decorations */}
            <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-purple-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
            <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-purple-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
            
            <div className="relative z-10">
              {/* Header */}
              <div className="flex items-start space-x-3 mb-8">
                <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-white">Assessment Reports</h2>
                  <p className="text-gray-300 text-sm">Comprehensive analysis and insights from AI assistants</p>
                </div>
              </div>

              {/* Loading state */}
              {loadingReports && (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
                </div>
              )}

              {/* Reports list */}
              {!loadingReports && reports.length > 0 && (() => {
                // Sort reports by date (most recent first)
                const sortedReports = [...reports].sort((a, b) => 
                  new Date(b.generated_at) - new Date(a.generated_at)
                )
                
                // Group reports by type
                const groupedReports = sortedReports.reduce((acc, report) => {
                  const type = report.type || 'Other'
                  if (!acc[type]) acc[type] = []
                  acc[type].push(report)
                  return acc
                }, {})
                
                return (
                  <div className="space-y-6 mb-8">
                    {Object.entries(groupedReports).map(([type, typeReports]) => (
                      <div key={type} className="space-y-3">
                        <h3 className="text-white/60 text-sm font-medium uppercase tracking-wider">{type}</h3>
                        <div className="space-y-3">
                          {typeReports.map((report) => (
                            <div key={report.report_id} className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </div>
                                <div>
                                  <h4 className="text-white font-medium">{report.title || report.filename}</h4>
                                  <p className="text-gray-400 text-sm">
                                    {new Date(report.generated_at).toLocaleDateString()} at {new Date(report.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • {(report.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleDownloadReport(report)}
                                className="px-4 py-2 bg-gradient-to-br from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg transition-all"
                              >
                                Download
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })()}

              {/* Empty state */}
              {!loadingReports && reports.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                  <div className="w-24 h-24 bg-gradient-to-br from-purple-500/10 to-purple-600/10 rounded-2xl flex items-center justify-center mb-6">
                    <svg className="w-12 h-12 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  
                  <h3 className="text-xl font-display font-light text-white mb-3">No Reports Yet</h3>
                  <p className="text-gray-400 text-center max-w-2xl mb-6">
                    Reports will be automatically generated by AI assistants as your idea completes the 5 main stages of assessment.
                  </p>

                {/* Stage list */}
                <div className="w-full max-w-2xl space-y-3">
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                    <div className="w-8 h-8 flex-shrink-0 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">1</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium text-sm">Opportunity Assessment</h4>
                      <p className="text-gray-400 text-xs">Market potential, demand, and competition analysis</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                    <div className="w-8 h-8 flex-shrink-0 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">2</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium text-sm">Commercial Ideation</h4>
                      <p className="text-gray-400 text-xs">Go-to-market strategies and competitive positioning</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                    <div className="w-8 h-8 flex-shrink-0 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">3</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium text-sm">Business Case Preparation</h4>
                      <p className="text-gray-400 text-xs">Revenue projections, cost models, and ROI analysis</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                    <div className="w-8 h-8 flex-shrink-0 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">4</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium text-sm">UX Design</h4>
                      <p className="text-gray-400 text-xs">User-centred designs and prototypes</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                    <div className="w-8 h-8 flex-shrink-0 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">5</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium text-sm">MVP Development</h4>
                      <p className="text-gray-400 text-xs">Build, test, and deploy to validate market fit</p>
                    </div>
                  </div>
                </div>

                  <p className="text-gray-500 text-sm mt-8 text-center">
                    As each stage completes, detailed reports will appear here with insights and recommendations.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </main>

      <RefineIdeaModal
        isOpen={isRefineModalOpen}
        onClose={() => setIsRefineModalOpen(false)}
        ideaId={currentIdea.id}
        refinementQuestions={currentIdea.triage_assessment?.refinement_questions}
        onRefinementComplete={async (updatedIdea) => {
          // Modal already closes itself via onClose(), so don't set state here
          // Just schedule the data refresh after a delay
          setTimeout(async () => {
            await refreshIdeaData()
            await loadIdeaHistory()
            
            // One more refresh after 2 more seconds
            setTimeout(async () => {
              await loadIdeaHistory()
            }, 2000)
          }, 1000)
        }}
      />

      <AuriHelpModal
        isOpen={isAuriHelpOpen}
        onClose={() => setIsAuriHelpOpen(false)}
        onCreateIdea={onCreateIdea}
      />

      <AuriFeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => {
          setIsFeedbackOpen(false)
          setFeedbackTeamMember(null)
        }}
        teamMember={feedbackTeamMember}
      />

      <AskOrinModal
        isOpen={isAskOrinOpen}
        onClose={() => setIsAskOrinOpen(false)}
        idea={currentIdea}
        onIdeaUpdated={(updates) => {
          setCurrentIdea(prev => ({ ...prev, ...updates }))
        }}
      />

      <Footer variant="authenticated" />
    </div>
  )
}

IdeaDetail.propTypes = {
  idea: PropTypes.shape({
    id: PropTypes.string.isRequired,
    what: PropTypes.string.isRequired,
    who: PropTypes.string.isRequired,
    why: PropTypes.string.isRequired,
    what_expected: PropTypes.string,
    make_money: PropTypes.bool,
    positive_examples: PropTypes.string,
    negative_examples: PropTypes.string,
    whiteboard_data: PropTypes.object,
    status: PropTypes.string.isRequired,
    created_at: PropTypes.string.isRequired,
    updated_at: PropTypes.string,
    openTasksTab: PropTypes.bool,
    highlightTaskId: PropTypes.string,
    pinned: PropTypes.bool,
    archived: PropTypes.bool,
    triage_assessment: PropTypes.shape({
      reasoning: PropTypes.string.isRequired,
      confidence: PropTypes.number.isRequired,
      assessment_date: PropTypes.string.isRequired
    }),
    search_terms: PropTypes.shape({
      search_terms: PropTypes.arrayOf(PropTypes.shape({
        term: PropTypes.string.isRequired,
        category: PropTypes.string.isRequired,
        priority: PropTypes.string.isRequired,
        rationale: PropTypes.string.isRequired
      })),
      total_terms: PropTypes.number,
      generated_at: PropTypes.string,
      model_used: PropTypes.string,
      categories: PropTypes.object
    })
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  onCreateIdea: PropTypes.func,
  onCreateIdeaChat: PropTypes.func,
  onLogout: PropTypes.func
}

export default IdeaDetail